/* kr-poster-style.js — city street posters drawn from our own Protomaps planet.

   Everything needed to draw a poster from a saved design lives here, so the
   editor (kr-poster-maker.js) and the print file render the SAME way:
     style(d, o)            the MapLibre style for a design's colors
     geom(d)                the poster's layout in design units
     drawOverlay(ctx,d,k,g) fade, marker, the plate and the credit over the map
     drawPlate(ctx,d,k,g)   the PLATE alone: the frame lines and the words
     drawPlateOff(ctx,d,k,g,o,dx,dy)  the plate with its red off register
     drawFinish(ctx,d,k,g)  the finish's texture over it all (film grain, risograph)
     (the print file: made on Kevin's PC by docs/poster-maker/print/)
     encode(d) / decode(s)  a design as a short string (cart property, URL hash)
     stateBox / statePlace / stateRecolor   a whole state's pre-rendered
                            layer: where it sits and how it takes the colours

   DESIGN UNITS: 100 per inch. A 13x19 poster is 1300x1900 units, and the map
   is a MapLibre map laid out at its rectangle in those units (CSS px), then
   CSS-scaled onto the screen. Line widths in the style are units too, so a
   street is the same fraction of the poster on a phone, a laptop and paper —
   the print file is the same map at pixelRatio 3 (300 DPI), same tiles, same
   geometry, only sharper.

   DETAIL (oz): a whole-city frame sits near z11, where the planet's tiles
   carry no residential streets; one zoom deeper they do. MapLibre refuses a
   vector source at any tileSize but 512, so the map is laid out oz times
   larger each way at zoom + log2(oz) and CSS-shrunk by 1/oz (the same move
   kr-press-basemap.js makes for book maps), with every width ×oz so it lands
   at its design size. oz 2 = one zoom deeper; a whole-city frame takes
   the z14 tiles, so every street of an old town is there (see ozFor). */
(function (root) {
  'use strict';
  if (!root || root.KRPoster) return;

  var UNITS_PER_IN = 100;
  var DEFAULT_PMTILES = 'https://krmaps-api.krmaps.workers.dev/tiles/planet-20260716.pmtiles';
  var CREDIT = '© OpenStreetMap contributors';
  /* ELEVATION (2026-10-26; Kevin: "How about adding mapterhorn elevation can
     I see what that would look like if turned on"): the self-hosted
     Mapterhorn archive the travel books shade their relief from (z0-12
     global, terrarium-encoded webp, on our R2; see kr-press-basemap.js),
     drawn as a MapLibre hillshade over the land, the parks and the
     buildings and under the streets, so a hill shades its blocks while the
     lines stay crisp. The sea is a flat plane in the archive, so the water
     takes no shading. Off unless a design asks (d.relief). */
  var DEFAULT_TERRAIN = 'https://krmaps-api.krmaps.workers.dev/tiles/mapterhorn-planet-20260511.pmtiles';
  /* judged on San Francisco, Salt Lake City, Fort Collins, Seattle and
     Innsbruck renders, light and dark sheets: MapLibre's standard shading
     (igor and combined read the same at a city frame), a touch past the
     books' 0.35-0.45 so a city's own hills show, not only the mountains
     behind it; the DEM at the design zoom (depth 0: a whole city is a dozen
     tiles, ~2 MB) */
  var RELIEF = { method: 'standard', exaggeration: 0.65, shadow: 0.55, highlight: 0.35, accent: 0.2,
    darkShadow: 0.55, darkHighlight: 0.2, depth: 0, direction: 315 };

  /* ── colors ───────────────────────────────────────────────────────────
     paper = the poster around the map · land / water / park / building fill
     the map · road = every street and path · ink = the title and second line
     · tag = the coordinates line · frame = the border lines · fadeTop /
     fadeBottom = the band the map dissolves into under the words (fadeMax =
     how far it covers the map, 1 = fully).

     The two KR classics are measured off the print files Kevin already sells
     (G:/WorldRoads/Masters, 13x19 at 600 DPI, 2023): BLACK & WHITE is
     NewYorkNewYork.tif — white land, water and ocean #e6e6e6 (the QGIS
     master's own fill), buildings #b0b0b0, roads pure black, parks left
     white, coordinates in grey #a8a8a8. The fade is WHITE since the 2025
     posters (Chicago: the map's ink is gone by 0.70H, the lake by 0.75H —
     the 2023 New York master faded into #e3e3e3 grey instead). BLUE is BluePragueCzech.tif — slate #404c60,
     roads #e6e6e6, buildings #727278, white words, and a fade that leaves the
     streets faintly showing behind them.
     New sets keep water light and leaning blue (Kevin's flavor rule); the
     wall-map sets keep their maps' own water so the poster matches the wall.
     Buildings sit a step from the land toward the roads and away from the
     water, so a built-up block never reads as a lake. The step started as
     Black & White's (Kevin, 2026-10-29, once its houses printed at the full
     #b0b0b0: "the buildings look great with this change. The rest of the
     flavors suffer just a bit from almost impossible to see buildings"):
     28 L* off the land, as far as his master's houses sit off white paper,
     never more than halfway to the set's small streets. Then every house one
     step darker (5.6 L*; Kevin, 2026-10-30: "buildings darker, less
     contrasted"): on a light sheet the blocks go deeper, toward the streets
     (stopping at 0.6 of the way to the small streets, so a street still
     reads through an old town packed with houses, Prague); on a dark sheet
     they sink back toward the sheet. Each set keeps its own hue for them.
     The two classics keep their masters' own tones. Before, the colored
     sets' houses sat 4-18 L* off the land, and half that at a city frame. */
  var THEMES = [
    /* Black & White prints the way Kevin's masters do (2026-10-29, beside
       his LosAngelesBWMetskers at 600 DPI: "the way mine physically prints
       at 600 dpi is much more interesting", then "I want the builder to
       display like test C"): every street and path in the full black ink,
       not the grey web, and the houses at the full #b0b0b0 at every zoom —
       the stippled block his blocks print with — not softened toward the
       paper on a wide frame */
    { key: 'bw', label: 'Black & White', group: 'classic',
      paper: '#ffffff', land: '#ffffff', water: '#e6e6e6', park: '#ffffff', building: '#b0b0b0',
      road: '#000000', minor: '#000000', pathInk: '#000000',
      ink: '#111111', tag: '#a8a8a8', frame: '#000000',
      fadeTop: '#ffffff', fadeBottom: '#ffffff', fadeMax: 1 },
    { key: 'blue', label: 'Blue', group: 'classic',
      paper: '#404c60', land: '#404c60', water: '#38435a', park: '#404c60', building: '#727278',
      road: '#e6e6e6', ink: '#fcfcfc', tag: '#b3b9c4', frame: '#e9e9e9',
      fadeTop: '#404c60', fadeBottom: '#404c60', fadeMax: 0.9 },

    /* TRAILHEAD (2026-10-27, from the "Outdoors City Map Flavor" design
       canvas; Kevin: "an outdoors style one that would pair well with the
       mapterhorn elevation", then "this will be a hiker's flavor they will
       want to see trails easily"): a park-service topo sheet — buff paper,
       sage woods, lake blue, umber lines, trail-sign orange for the
       coordinates. The set carries its own map treatments (drawn by
       style(), nothing else to switch on):
         trails   hiking trails, tracks and bridleways in the coordinates
                  orange, dashed, heavier; footways and cycleways stay
                  faint dashes in the path ink (every path in orange
                  speckled a city grid with its sidewalk paths)
         woods    forest and wood a step deeper than the parks
         contours elevation lines off the Mapterhorn archive, in the ink
                  (every fifth heavier); 20 m at a neighbourhood frame,
                  50 m at a city, 100 m wider
         summits  the twelve most prominent local maxima of the same
                  elevation grid, a triangle in the coordinates orange
       relief: the set asks for the shaded hills when it is picked (the
       light sheet shades in its darkest ink, so hills, streets and words
       are one umber pen) — the customer can still turn them off.
       First of the colors (Kevin, 2026-10-27: "put trailhead near the top
       of the flavor list"). */
    { key: 'trailhead', label: 'Trailhead', group: 'colors', relief: true,
      paper: '#e4ddc6', land: '#e4ddc6', water: '#a6c3d1', park: '#c3cba6', building: '#999072',
      road: '#4a3826', minor: '#735d44', pathInk: '#9c8a69', ink: '#3a2b1d', tag: '#b35a2a', frame: '#4a3826',
      trails: true, woods: '#b2bd94', contours: true, summits: true },
    /* ATLAS (Kevin, 2026-10-27, four plates of Paris: "build a flavor
       ... thats a similar concept to this"): cream paper, the houses a soft
       tan and the big buildings ochre, parks sage, the Seine jade with a
       dark teal shore, terracotta mains over a salmon street web, town lines
       dash-dot in slate, railways a grey dash; the words in the shore's
       teal, the coordinates in terracotta. Four treatments of its own
       (shore, landmark, rail, bounds — see style()). The "Atlas Flavor"
       design canvas holds the plates beside the renders. */
    { key: 'atlas', label: 'Atlas', group: 'colors',
      paper: '#f5efe3', land: '#f5efe3', water: '#a4c6b9', park: '#dfe1cf', building: '#cbc2af',
      road: '#c4704e', minor: '#d4987e', pathInk: '#e0bfae', ink: '#3b5f63', tag: '#c4704e', frame: '#3b5f63',
      woods: '#d0d6b9', shore: '#3d6568', landmark: '#e2c58d', rail: '#9aa39b', bounds: '#2c4652' },
    /* THE 2026-10-25 ROUND (the "City Poster Styles" design canvas: six
       sets judged as real renders beside the thirteen, Kevin: "Build all six
       into the maker. replace any that are almost the same style that
       already exist"): Antique took Ink on Cream's place (dark ink on cream
       twice); Blueprint, Crimson, Evergreen, Burgundy and Night Lights are
       new. Night Lights was first put in Navy & Gold's place, but Kevin:
       "i also liked the navy and gold" — both stay. The retired key still
       answers (RETIRED, below). */
    { key: 'charcoal', label: 'Charcoal', group: 'colors',
      paper: '#24272b', land: '#24272b', water: '#363d45', park: '#2a2e33', building: '#57595c',
      road: '#e8e6e1', ink: '#f2f0eb', frame: '#d9d6cf' },
    /* sepia ink on aged paper, the water a dusty grey-teal, the parks khaki:
       an old survey sheet (in Ink on Cream's slot) */
    { key: 'antique', label: 'Antique', group: 'colors',
      paper: '#ebe1cb', land: '#ebe1cb', water: '#c9d4d2', park: '#e0d6b8', building: '#988b6d',
      road: '#5b4433', ink: '#4a3727', tag: '#9a8a75', frame: '#5b4433' },
    { key: 'sage', label: 'Sage', group: 'colors',
      paper: '#e2e7dc', land: '#e2e7dc', water: '#c3d6db', park: '#d4dccb', building: '#8a9086',
      road: '#2d3a2f', ink: '#2d3a2f', frame: '#2d3a2f' },
    { key: 'terracotta', label: 'Terracotta', group: 'colors',
      paper: '#b4644b', land: '#b4644b', water: '#d6c7c4', park: '#a95d45', building: '#d08c72',
      road: '#f7e9dc', ink: '#fbf1e8', frame: '#f7e9dc' },
    { key: 'navy_gold', label: 'Navy & Gold', group: 'colors',
      paper: '#1d2940', land: '#1d2940', water: '#2c3c59', park: '#213049', building: '#4f5768',
      road: '#d5b36b', ink: '#dcbf7d', frame: '#d5b36b' },
    { key: 'blush', label: 'Blush', group: 'colors',
      paper: '#f5e6de', land: '#f5e6de', water: '#dde3ea', park: '#ecd8ce', building: '#a38e86',
      road: '#5a3a35', ink: '#5a3a35', frame: '#5a3a35' },
    { key: 'ocean', label: 'Ocean', group: 'colors',
      paper: '#ffffff', land: '#ffffff', water: '#b7d5e4', park: '#eef3ef', building: '#aaaaaa',
      road: '#1f4b66', ink: '#1f4b66', frame: '#1f4b66' },
    /* white lines on cobalt, the drafting sheet; the fade at 0.9 like Blue's */
    { key: 'blueprint', label: 'Blueprint', group: 'colors',
      paper: '#22427c', land: '#22427c', water: '#1b3669', park: '#213f78', building: '#5976b0',
      road: '#f3f6fb', ink: '#ffffff', tag: '#a9bbda', frame: '#e4ebf7',
      fadeTop: '#22427c', fadeBottom: '#22427c', fadeMax: 0.9 },
    /* one red ink on warm white, the water kept a pale blue */
    { key: 'crimson', label: 'Crimson', group: 'colors',
      paper: '#faf6f0', land: '#faf6f0', water: '#dfe7ec', park: '#f2ece3', building: '#aea59b',
      road: '#a3211d', ink: '#8e1c19', tag: '#b89b94', frame: '#a3211d' },
    /* cream lines on forest green, the water a darker green */
    { key: 'evergreen', label: 'Evergreen', group: 'colors',
      paper: '#1f3b2d', land: '#1f3b2d', water: '#172e23', park: '#23402f', building: '#50715e',
      road: '#ebe6d4', ink: '#f3eee0', tag: '#a6b3a5', frame: '#ebe6d4' },
    /* blush lines on deep wine, the blocks a plum a step up from the sheet */
    { key: 'burgundy', label: 'Burgundy', group: 'colors',
      paper: '#4a1c28', land: '#4a1c28', water: '#3a1420', park: '#471a29', building: '#864c5b',
      road: '#f3e3dc', ink: '#f8ede8', tag: '#b99aa1', frame: '#f3e3dc' },
    /* amber avenues over a cool grey web of side streets on near-black: the
       city at night. The one set with inks of its own for the side streets
       and paths (minor, pathInk) instead of the road ink eased toward the
       land — so its style card can't be mixed from the class render; the
       maker renders that card on its own. The fade stops at 0.9, as Blue's
       does, so faint streets stay under the words. */
    { key: 'night', label: 'Night Lights', group: 'colors',
      paper: '#15181f', land: '#15181f', water: '#0d0f15', park: '#181c24', building: '#3a3f4a',
      road: '#e8b45a', minor: '#7f8797', pathInk: '#4b5260', ink: '#f6ead0', tag: '#8f98a7', frame: '#d9c9a2',
      fadeTop: '#15181f', fadeBottom: '#15181f', fadeMax: 0.9 },

    /* THE 2026-10-25 EVENING ROUND (the "Funky City Map Styles" design
       canvas: six candidates judged as real renders; Kevin: "pop and neon
       are cool. Riso has potential same with Bauhaus they just need a better
       color palette something that feel cohesive and thought out", then
       "build them with the 70 percent bauhaus no need for acid or miami").
       Four sets with lots of colour and high contrast, every one with inks
       of its own for the side streets and paths (minor, pathInk) and a block
       colour of its own, so each card is a render of its own, as Night
       Lights' is. */
    /* a lemon sheet, tangerine blocks, lime parks and turquoise water: the
       avenues in indigo over a magenta web of streets, violet paths */
    { key: 'pop', label: 'Pop', group: 'colors',
      paper: '#ffe14a', land: '#ffe14a', water: '#39c7e5', park: '#6fdc5a', building: '#ea892b',
      road: '#2a1a6b', minor: '#e0218a', pathInk: '#7a4fd6', ink: '#2a1a6b', tag: '#e0218a', frame: '#2a1a6b' },
    /* the city at night in neon: cyan avenues over a magenta web on
       violet-black, dusky violet blocks, dark green parks, a navy harbour,
       the name in acid yellow; the fade stops at 0.9, as Night Lights' does */
    { key: 'neon', label: 'Neon', group: 'colors',
      paper: '#0d0a1f', land: '#0d0a1f', water: '#0a1630', park: '#0e1f1a', building: '#433667',
      road: '#22e5ff', minor: '#ff3fcf', pathInk: '#7c5cff', ink: '#f4ff5e', tag: '#9b9fc4', frame: '#22e5ff',
      fadeTop: '#0d0a1f', fadeBottom: '#0d0a1f', fadeMax: 0.9 },
    /* three risograph inks on natural paper (Blue #0078bf, Fluorescent Pink
       #ff48b0, Sunflower #ffb511) and every colour one ink, full or at a tint
       of the paper: avenues and words Blue, streets Pink, paths Pink 55 %,
       blocks Pink 20 %, water Blue 35 %, parks Sunflower 40 % (a first take
       with a hue per layer read as arbitrary to Kevin). Since 2026-10-29 the
       blocks are that Pink 20 % taken a step deeper and duskier, so they
       show the way every set's houses now do (see the THEMES note): a deeper
       Pink tint washed the whole sheet pink and sank the pink streets into
       it. */
    { key: 'riso', label: 'Riso', group: 'colors',
      paper: '#f4efe4', land: '#f4efe4', water: '#9fc5d7', park: '#f8d890', building: '#d3adb8',
      road: '#0078bf', minor: '#ff48b0', pathInk: '#fa93c7', ink: '#0078bf', tag: '#ff48b0', frame: '#0078bf' },
    /* one job per primary: red the avenues and yellow the parks at full
       strength, blue the water, softened to the Bauhaus blue #1c6dd0 at a
       70 % tint of the sheet (Kevin: "The water really needs a softer blue
       imo"; he took 70 from a ladder of 70/55/42/30); black streets and
       words, warm grey blocks and paths, a warm white sheet. Its blocks sit
       lighter than the other sets' (Kevin, 2026-10-30: "bauhaus looks better
       with the lighter shade buildings tho", then at Black & White's own
       28 L* step, "still too dark"; he picked No. 3 of a ladder of four):
       18 L* off the sheet, a warm grey a step lighter than B&W's #b0b0b0
       under the same black streets */
    { key: 'bauhaus', label: 'Bauhaus', group: 'colors',
      paper: '#f5f2eb', land: '#f5f2eb', water: '#5d95d8', park: '#f3c315', building: '#c4c0b3',
      road: '#e02e1f', minor: '#1a1a1a', pathInk: '#8c8880', ink: '#1a1a1a', tag: '#e02e1f', frame: '#1a1a1a' },

    /* THE 2026-10-26 ROUND (the "2026 Palettes and Finishes" design canvas:
       seven palettes, each with a finish, judged as real renders; Kevin:
       "Mocha and teal and risograph I would like to see in a live preview
       harness"). The first sets that carry a FINISH of their own (FINISHES,
       below); a design can take another, or none. */
    /* a chocolate sheet, cream avenues over a latte web of streets, the
       coordinates in caramel; film grain over all */
    { key: 'mocha', label: 'Mocha', group: 'colors', finish: 'film',
      paper: '#3b2b27', land: '#3b2b27', water: '#2a2123', park: '#43332c', building: '#755e56',
      road: '#f2e5d6', minor: '#cdb7a6', pathInk: '#8f786b', ink: '#f6ecdf', tag: '#d8a26e', frame: '#e2cdb8',
      fadeTop: '#3b2b27', fadeBottom: '#3b2b27', fadeMax: 0.9 },
    /* WGSN's 2026 color, Transformative Teal, as the sheet; cream avenues
       over a mint web, the coordinates in fluorescent pink; printed as a
       risograph (its red plate off register, screen grain, paper mottle) */
    { key: 'teal', label: 'Teal', group: 'colors', finish: 'risograph',
      paper: '#176a6b', land: '#176a6b', water: '#115457', park: '#1c7472', building: '#449392',
      road: '#eef5ee', minor: '#a9d9cf', pathInk: '#67a39d', ink: '#f3f9f3', tag: '#ff66b8', frame: '#d2ebe3',
      fadeTop: '#176a6b', fadeBottom: '#176a6b', fadeMax: 0.9 },

    /* the wall maps' own palettes (kr-press-basemap.js flavors of the same
       names), so a city poster can hang beside the push-pin map it matches.
       Slated Earth was one of them until 2026-10-25 (Kevin: "slated earth
       needs to be removed"); its key opens Executive, the nearest look. */
    { key: 'muted_tones', label: 'Muted Tones', group: 'wall',
      paper: '#fffcf5', land: '#eee7d3', water: '#fffcf5', park: '#d8e2cb', building: '#969081',
      road: '#33302b', ink: '#33302b', frame: '#33302b' },
    { key: 'heartstrings', label: 'Heartstrings', group: 'wall',
      paper: '#f5f3ee', land: '#d4b7a6', water: '#efece8', park: '#d9d0c2', building: '#806759',
      road: '#3b2a25', ink: '#1a1715', frame: '#1a1715' },
    { key: 'executive', label: 'Executive', group: 'wall',
      paper: '#e0cfb4', land: '#d3bfa7', water: '#5a7580', park: '#c7b89b', building: '#776855',
      road: '#2b2118', ink: '#2b2118', frame: '#2b2118' }
  ];
  var THEME_GROUPS = [
    { id: 'classic', label: 'Classic' },
    { id: 'colors', label: 'More colors' },
    { id: 'wall', label: 'To match our wall maps' }
  ];
  var THEME_BY = {};
  THEMES.forEach(function (t) { THEME_BY[t.key] = t; });
  /* the sets the 2026-10-25 round retired, each answered by the nearest
     look that stayed: an old link (the home page's ?color=cream thumb) or a
     saved design still opens. The maker and the print tool write the live
     key back into the design (theme(key).key). */
  var RETIRED = { cream: 'antique', slated_earth: 'executive' };
  function themeByKey(k) { return THEME_BY[k] || THEME_BY[RETIRED[k]] || null; }

  /* ── print effects: a surface over the whole poster ──────────────────
     (2026-10-26, from the "2026 Palettes and Finishes" design canvas.
     Seven were tried as real renders and in a live preview; Kevin kept two:
     "Only riso and film grain are worth keeping honestly". Paper, Linen,
     Dot screen, Color wash and Glow were dropped.) An effect is drawn over
     the finished poster (map, frame and words) in DESIGN UNITS, so the
     editor, the style cards and the print file carry the same surface,
     each at its own resolution:
       grain   one random value per design unit (0.25 mm on paper): the
               same values everywhere (a seeded tile 4.8 in square,
               repeated), smoothed up at print resolution and AVERAGED down
               on a screen, where it fades the way it does across a room;
               the editor's lock brings it back up close
       paper   a cotton mottle under the risograph's ink: value noise at 12
               and 4 units over that grain
       misreg  a risograph's red plate off register by dx/dy units, on
               the PLATE only (the frame lines and the words: drawPlate);
               the map, the fade, the marker and the credit stay on
               register (Kevin, 2026-10-26, holding the first real print:
               "the chromatic aberration effect ... should just be applied
               to the lettering and outside border rather than the actual
               map features"). The print and the cards composite the plate
               twice (drawPlateOff: its red from a copy drawn dx/dy off);
               the editor shows it on two blended canvases
               (kr-poster-maker.js paintPlate).
     The order everywhere: the map, the plate (off register or not), then
     the layers over all.
     A color set may carry its own (finish: 'film'); a design may name one
     (d.finish), 'none' included, and that wins. The editor keeps the
     choice under "Show advanced options" in the Style step ("keep them
     somewhere more like an advanced area"). */
  var FINISHES = [
    { key: 'none', label: 'None', layers: [] },
    /* grain only: the vignette it carried on the canvas (the sheet 28 %
       darker toward its edges) came off on Kevin's word, "I like the film
       grain but I dont love how it gives a gradient look around the border" */
    { key: 'film', label: 'Film grain', layers: [
      { kind: 'grain', amp: 46, blend: 'overlay', alpha: 0.32 }
    ] },
    /* the red plate 2.5 units off (0.64 mm; 15 px at 600 dpi), half that
       down, on the plate only. It was 2 units on the WHOLE sheet on the
       canvas renders ("its actually too much when looking at up close":
       every thin street split into a red and a cyan copy), then 0.75 on
       the whole sheet, which the first real print showed as nothing from a
       chair ("hard to see on the actual print"). With the streets on
       register the words and the frame can carry a fringe that reads from
       across a room; at 4 units the coordinates line splits in two (the
       design canvas "Risograph fringe on lettering and border", 10-26) */
    { key: 'risograph', label: 'Risograph', misreg: { dx: 2.5, dy: 1.25 }, layers: [
      { kind: 'grain', amp: 48, blend: 'screen', alpha: 0.2 },
      { kind: 'paper', blend: 'multiply', alpha: 0.14 }
    ] }
  ];
  var FINISH_BY = {};
  FINISHES.forEach(function (f) { FINISH_BY[f.key] = f; });
  function finishOf(d, t) {
    var k = d && d.finish;
    if (k && FINISH_BY[k]) return FINISH_BY[k];
    t = t || themeOf(d);
    return FINISH_BY[t && t.finish] || FINISH_BY.none;
  }
  /* the texture tile: TILE design units square, a whole number of the
     paper's 12- and 4-unit cells, so it repeats without a seam; a screen
     averages it in blocks of a divisor of TILE */
  var TILE = 480;
  var TILE_DIVS = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 16, 20, 24, 30, 40, 48, 60, 80, 96, 120, 160, 240, 480];
  function hash01(x, y, s) {
    var h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(s | 0, 0x9e3779b1);
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  }
  /* smooth value noise on a lattice every `cell` units, wrapping at TILE */
  function valueNoise(u, v, cell, seed) {
    var n = TILE / cell, x = u / cell, y = v / cell, x0 = Math.floor(x), y0 = Math.floor(y);
    var fx = x - x0, fy = y - y0, x1 = (x0 + 1) % n, y1 = (y0 + 1) % n;
    x0 %= n; y0 %= n;
    var a = hash01(x0, y0, seed), b = hash01(x1, y0, seed), c = hash01(x0, y1, seed), e = hash01(x1, y1, seed);
    var sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
    var top = a + (b - a) * sx, bot = c + (e - c) * sx;
    return top + (bot - top) * sy;
  }
  /* one value per design unit across the tile, 0-255 grey */
  var _unitTex = {};
  function unitTexture(L) {
    var key = L.kind + ':' + (L.amp || 0);
    if (_unitTex[key]) return _unitTex[key];
    var a = new Float32Array(TILE * TILE), i = 0;
    for (var v = 0; v < TILE; v++) for (var u = 0; u < TILE; u++, i++) {
      var val;
      if (L.kind === 'grain') val = 128 + (hash01(u, v, 11) * 2 - 1) * L.amp;
      else {
        /* paper: three octaves multiplied into white, then lifted toward it */
        var o12 = (200 + (valueNoise(u, v, 12, 23) * 2 - 1) * 55) / 255;
        var o4 = (200 + (valueNoise(u, v, 4, 37) * 2 - 1) * 55) / 255;
        var o1 = (200 + (hash01(u, v, 41) * 2 - 1) * 55) / 255;
        var p = (0.5 + 0.5 * o12) * (0.65 + 0.35 * o4) * (0.7 + 0.3 * o1);
        val = 255 * (0.55 + 0.45 * p);
      }
      a[i] = val < 0 ? 0 : val > 255 ? 255 : val;
    }
    return (_unitTex[key] = a);
  }
  /* the tile as a canvas, n design units per pixel (1 where the target
     draws a unit a pixel or larger; else each pixel the AVERAGE of n x n
     units: a screen too coarse for the grain sees its average, never a
     coarser grain) */
  var _tileCv = {};
  function finishTile(L, k) {
    var n = 1;
    if (k < 1) {
      var want = 1 / k;
      TILE_DIVS.forEach(function (m) { if (Math.abs(Math.log(m / want)) < Math.abs(Math.log(n / want))) n = m; });
    }
    var key = L.kind + ':' + (L.amp || 0) + ':' + n;
    if (_tileCv[key]) return _tileCv[key];
    var src = unitTexture(L), side = TILE / n, nn = n * n;
    var cv = root.document.createElement('canvas');
    cv.width = cv.height = side;
    var cx = cv.getContext('2d'), im = cx.createImageData(side, side), b = im.data;
    for (var y = 0; y < side; y++) for (var x = 0; x < side; x++) {
      var sum = 0;
      for (var j = 0; j < n; j++) { var r0 = (y * n + j) * TILE + x * n; for (var q = 0; q < n; q++) sum += src[r0 + q]; }
      var o = (y * side + x) * 4;
      b[o] = b[o + 1] = b[o + 2] = sum / nn; b[o + 3] = 255;
    }
    cx.putImageData(im, 0, 0);
    return (_tileCv[key] = { cv: cv, n: n });
  }
  /* one effect layer over the whole sheet, onto a context whose transform
     maps device px (as drawOverlay's does; k = device px per design unit).
     blend: the layer's own (the print, the cards) or 'source-over' (the
     editor, whose layer canvases are blended into the poster by CSS) */
  function drawFinishLayer(ctx, L, k, g, blend) {
    ctx.save();
    ctx.scale(k, k);
    ctx.globalCompositeOperation = blend || L.blend;
    var t = finishTile(L, k), pat = ctx.createPattern(t.cv, 'repeat');
    if (pat.setTransform && root.DOMMatrix) pat.setTransform(new root.DOMMatrix([t.n, 0, 0, t.n, 0, 0]));
    ctx.imageSmoothingEnabled = true;
    ctx.globalAlpha = L.alpha == null ? 1 : L.alpha;
    ctx.fillStyle = pat;
    ctx.fillRect(0, 0, g.W, g.H);
    ctx.restore();
  }
  /* the effect's layers over a poster drawn at k device px per unit (the
     plate's misregistration is the caller's, before this: drawPlateOff) */
  function drawFinish(ctx, d, k, g, o) {
    o = o || {};
    g = g || geom(d);
    var f = o.finish || finishOf(d, o.theme);
    f.layers.forEach(function (L) { drawFinishLayer(ctx, L, k, g); });
    return f;
  }
  /* THE PLATE OFF REGISTER (a risograph's red ink printed dx/dy device px
     right and down of its other inks), onto a context whose transform
     places the sheet's device px (a print band's, a card's). The plate is
     rasterized twice, in place and shifted (any shift: a fraction of a
     pixel on a style card, fifteen pixels on a 600 dpi print), and each
     pixel takes its green and blue from the plate in place and its red
     from the shifted plate, over what the context holds (the map, the
     fade). The rows go through in chunks, so a print band never holds the
     whole plate twice, and every chunk's plates are drawn from the sheet,
     so chunks and bands register. */
  function drawPlateOff(ctx, d, k, g, o, dx, dy) {
    var m = ctx.getTransform ? ctx.getTransform() : { e: 0, f: 0 };
    var cw = ctx.canvas.width, ch = ctx.canvas.height, step = 256;
    var pc = root.document.createElement('canvas');
    pc.width = cw; pc.height = Math.min(step, ch);
    var px = pc.getContext('2d', { willReadFrequently: true });
    function plateRows(yy, h, ox, oy) {
      px.setTransform(1, 0, 0, 1, 0, 0);
      px.clearRect(0, 0, cw, h);
      px.setTransform(1, 0, 0, 1, m.e + ox, m.f + oy - yy);
      drawPlate(px, d, k, g, o);
      return px.getImageData(0, 0, cw, h).data;
    }
    for (var yy = 0; yy < ch; yy += step) {
      var h = Math.min(step, ch - yy);
      if (pc.height !== h) pc.height = h;
      var inPlace = plateRows(yy, h, 0, 0), shifted = plateRows(yy, h, dx, dy);
      var im = ctx.getImageData(0, yy, cw, h), a = im.data;
      for (var i = 0; i < a.length; i += 4) {
        var a1 = inPlace[i + 3], a2 = shifted[i + 3];
        if (a1 === 255) { a[i + 1] = inPlace[i + 1]; a[i + 2] = inPlace[i + 2]; }
        else if (a1) {
          a[i + 1] = (inPlace[i + 1] * a1 + a[i + 1] * (255 - a1) + 127) / 255 | 0;
          a[i + 2] = (inPlace[i + 2] * a1 + a[i + 2] * (255 - a1) + 127) / 255 | 0;
        }
        if (a2 === 255) a[i] = shifted[i];
        else if (a2) a[i] = (shifted[i] * a2 + a[i] * (255 - a2) + 127) / 255 | 0;
      }
      ctx.putImageData(im, 0, yy);
    }
  }

  /* the thumbnail renderer's class palette: one GL render in these pure
     colors, then every theme is mixed per pixel (see recolor) — land red,
     water green, park blue, roads black, so a pixel's RGB IS its mix.
     Buildings render as 56% land + 44% road: the middle of the mixed sets'
     own house tones, which sit a step from their land toward their roads
     (26-33% of the way on the dark sheets, 44-56% on the light; see
     THEMES). */
  var CLASS_THEME = { paper: '#ff0000', land: '#ff0000', water: '#00ff00', park: '#0000ff',
    building: '#8e0000', road: '#000000', ink: '#000000', frame: '#000000',
    /* small streets 70% road and paths 45%: the middle of the themes' own
       eases (see minorInk), so one class render stands for every card */
    minor: '#4d0000', pathInk: '#8c0000',
    fadeTop: '#ff0000', fadeBottom: '#ff0000', fadeMax: 1 };

  /* ── lettering ────────────────────────────────────────────────────────
     track = letter spacing in em. fit = the title's width as a fraction of
     the poster's: Kevin's masters letter every city to the same width
     (NEW YORK, LONDON and PRAGUE all span ~55% of a 13" sheet), so the
     spacing opens up for short names and closes for long ones, between
     minTrack and maxTrack; a name too long even at minTrack shrinks.
     CLASSIC is calibrated on NewYorkNewYork.tif in Barlow (the DIN-like
     Google face): title cap height 0.0738 of the width at 0.1054W, second
     line Barlow Light at 0.074em (UNITED KINGDOM and CZECHIA land within 1%
     of the masters), coordinates Barlow Light at 0.107em. */
  var FONTS = [
    { key: 'classic', label: 'Classic', css: 'Barlow',
      title: { family: 'Barlow', weight: 500, track: 0.0715, fit: 0.554, minTrack: 0.04, maxTrack: 0.38, caps: true },
      sub: { family: 'Barlow', weight: 300, track: 0.0738, caps: true },
      tag: { family: 'Barlow', weight: 300, track: 0.107, caps: false } },
    { key: 'modern', label: 'Modern', css: 'Josefin Sans',
      title: { family: 'Josefin Sans', weight: 600, track: 0.12, fit: 0.554, minTrack: 0.06, maxTrack: 0.4, caps: true },
      sub: { family: 'Josefin Sans', weight: 400, track: 0.22, caps: true },
      tag: { family: 'Josefin Sans', weight: 400, track: 0.1, caps: false } },
    { key: 'serif', label: 'Serif', css: 'Cormorant Garamond',
      title: { family: 'Cormorant Garamond', weight: 600, track: 0.08, fit: 0.554, minTrack: 0.04, maxTrack: 0.36, caps: true },
      sub: { family: 'Cormorant Garamond', weight: 500, track: 0.02, caps: false, italic: true, scale: 1.25 },
      tag: { family: 'Cormorant Garamond', weight: 500, track: 0.08, caps: false } },
    /* CONDENSED: Kevin's current posters (Chicago 2025, San Francisco 2026)
       letter in Bebas Neue Pro — Regular title, Book subtitle. Bebas Neue
       (Google, OFL) is the free cut of the same face for the title; Oswald
       Light stands in for the Pro's lowercase Book. Sized and spaced off
       the Chicago poster: title cap height 0.090 of the width with fixed
       spacing (SAN FRANCISCO runs wider than CHICAGO), "Illinois" 0.047S,
       coordinates 0.028S. */
    { key: 'condensed', label: 'Condensed', css: 'Bebas Neue',
      title: { family: 'Bebas Neue', weight: 400, track: 0.1816, caps: true, scale: 1.216 },
      sub: { family: 'Oswald', weight: 300, track: 0.1974, caps: false, scale: 1.033 },
      tag: { family: 'Barlow', weight: 300, track: 0.135, caps: false, scale: 1.029 } },
    { key: 'script', label: 'Script', css: 'Great Vibes',
      title: { family: 'Great Vibes', weight: 400, track: 0, caps: false, scale: 1.3 },
      sub: { family: 'Barlow', weight: 300, track: 0.12, caps: true },
      tag: { family: 'Barlow', weight: 300, track: 0.107, caps: false } }
  ];
  var FONT_BY = {};
  FONTS.forEach(function (f) { FONT_BY[f.key] = f; });

  /* ── local lettering ──────────────────────────────────────────────────
     A place whose country writes in a script of its own can letter its
     poster in it (Kevin, 2026-10-26: "for places like Tokyo or applicable
     countries can we have a choice to display the lettering to Japanese as
     an option as a lettering option"): one more Lettering card, offered
     only for such a place — the title and second line in the place's own
     spelling (東京 / 日本), in a Noto face drawn for the script. The
     coordinates line keeps its own face. The countries are the book
     studio's (kr-press-studio-core.js CC_LANG): only where ONE script is the
     clear voice of the place, so India (many scripts) and Belarus (two
     languages) sit out rather than be guessed wrong.
       lang  the BCP 47 tag: capitals (Greek drops its accents in capitals)
             and the country's own name come from the browser's locale data
       geo   the geocoder's language for the place's own name
       test  a word written in the script
     The faces come from Google Fonts on demand (gf): a CJK face arrives in
     slices of characters, so loadFonts asks for the very characters drawn.
     The kind sets the type: CJK names are two or three characters (東京,
     서울, 北京), spaced out the way Classic spaces TOKYO, at weight 400 —
     the stem of Classic's Barlow 500 (judged side by side at 120 DPI; a
     square kanji at Classic's size stands a fifth taller than its capitals,
     hence 0.92); Greek and Cyrillic letter in capitals like Classic; a
     script whose marks stack on its letters (Thai, Khmer, Lao, Burmese) or
     join them (Arabic) is never letter-spaced. */
  var LOCAL_KINDS = {
    cjk: { title: { weight: 400, track: 0.5, fit: 0.29, minTrack: 0.15, maxTrack: 1.2, scale: 0.92 },
      sub: { weight: 300, track: 0.5, scale: 0.9 } },
    caps: { title: { weight: 500, track: 0.0715, fit: 0.554, minTrack: 0.04, maxTrack: 0.38, caps: true },
      sub: { weight: 300, track: 0.0738, caps: true } },
    joined: { title: { weight: 500, track: 0, scale: 0.86 }, sub: { weight: 300, track: 0, scale: 0.9 } },
    /* Arabic reaches further above and below its line than Thai: a size
       down, so a descender (دبي) clears the second line */
    arabic: { title: { weight: 500, track: 0, scale: 0.8 }, sub: { weight: 300, track: 0, scale: 0.86 } },
    hebrew: { title: { weight: 500, track: 0.1, fit: 0.42, minTrack: 0.04, maxTrack: 0.3 }, sub: { weight: 300, track: 0.08 } }
  };
  var LOCAL = [
    { key: 'ja', label: 'Japanese', native: '日本語', lang: 'ja', geo: 'ja', kind: 'cjk', family: 'Noto Sans JP',
      test: /[\p{sc=Han}\p{sc=Hiragana}\p{sc=Katakana}]/u },
    { key: 'ko', label: 'Korean', native: '한국어', lang: 'ko', geo: 'ko', kind: 'cjk', family: 'Noto Sans KR',
      test: /\p{sc=Hangul}/u },
    { key: 'zh-Hans', label: 'Chinese', native: '中文', lang: 'zh-Hans', geo: 'zh', kind: 'cjk', family: 'Noto Sans SC',
      test: /\p{sc=Han}/u },
    { key: 'zh-Hant', label: 'Chinese', native: '中文', lang: 'zh-Hant', geo: 'zh', kind: 'cjk', family: 'Noto Sans TC',
      test: /\p{sc=Han}/u },
    { key: 'th', label: 'Thai', native: 'ไทย', lang: 'th', geo: 'th', kind: 'joined', family: 'Noto Sans Thai',
      test: /\p{sc=Thai}/u },
    { key: 'km', label: 'Khmer', native: 'ខ្មែរ', lang: 'km', geo: 'km', kind: 'joined', family: 'Noto Sans Khmer',
      test: /\p{sc=Khmer}/u },
    { key: 'lo', label: 'Lao', native: 'ລາວ', lang: 'lo', geo: 'lo', kind: 'joined', family: 'Noto Sans Lao',
      test: /\p{sc=Lao}/u },
    { key: 'my', label: 'Burmese', native: 'မြန်မာ', lang: 'my', geo: 'my', kind: 'joined', family: 'Noto Sans Myanmar',
      test: /\p{sc=Myanmar}/u },
    { key: 'el', label: 'Greek', native: 'Ελληνικά', lang: 'el', geo: 'el', kind: 'caps', family: 'Noto Sans',
      test: /\p{sc=Greek}/u },
    { key: 'ru', label: 'Russian', native: 'Русский', lang: 'ru', geo: 'ru', kind: 'caps', family: 'Noto Sans',
      test: /\p{sc=Cyrillic}/u },
    { key: 'uk', label: 'Ukrainian', native: 'Українська', lang: 'uk', geo: 'uk', kind: 'caps', family: 'Noto Sans',
      test: /\p{sc=Cyrillic}/u },
    { key: 'bg', label: 'Bulgarian', native: 'Български', lang: 'bg', geo: 'bg', kind: 'caps', family: 'Noto Sans',
      test: /\p{sc=Cyrillic}/u },
    { key: 'sr', label: 'Serbian', native: 'Српски', lang: 'sr-Cyrl', geo: 'sr', kind: 'caps', family: 'Noto Sans',
      test: /\p{sc=Cyrillic}/u },
    { key: 'ar', label: 'Arabic', native: 'العربية', lang: 'ar', geo: 'ar', kind: 'arabic', family: 'Noto Kufi Arabic',
      test: /\p{sc=Arabic}/u },
    { key: 'he', label: 'Hebrew', native: 'עברית', lang: 'he', geo: 'he', kind: 'hebrew', family: 'Noto Sans Hebrew',
      test: /\p{sc=Hebrew}/u }
  ];
  var CC_LOCAL = {
    JP: 'ja', KR: 'ko', KP: 'ko', CN: 'zh-Hans', TW: 'zh-Hant', HK: 'zh-Hant', MO: 'zh-Hant',
    TH: 'th', KH: 'km', LA: 'lo', MM: 'my',
    GR: 'el', CY: 'el', RU: 'ru', UA: 'uk', BG: 'bg', RS: 'sr',
    SA: 'ar', AE: 'ar', QA: 'ar', KW: 'ar', BH: 'ar', OM: 'ar', JO: 'ar', LB: 'ar', IQ: 'ar',
    EG: 'ar', MA: 'ar', DZ: 'ar', TN: 'ar', LY: 'ar', YE: 'ar', SY: 'ar', PS: 'ar',
    IL: 'he'
  };
  LOCAL.forEach(function (L) {
    var k = LOCAL_KINDS[L.kind];
    L.local = true;
    /* the variable face: every weight real (a sheet of named weights
       hands 400 the 500), a third of the stylesheet */
    L.gf = L.family.replace(/ /g, '+') + ':wght@100..900';
    L.rtl = L.key === 'ar' || L.key === 'he';
    L.title = Object.assign({ family: L.family, caps: false, lang: L.lang }, k.title);
    L.sub = Object.assign({ family: L.family, caps: false, lang: L.lang }, k.sub);
    /* the coordinates keep Classic's face; anything else typed there in
       the script still draws in the script's face */
    L.tag = { family: 'Barlow', weight: 300, track: 0.107, caps: false, fallback: L.family };
    FONT_BY[L.key] = L;
  });
  /* the local lettering for a country (ISO 3166 two-letter code), or null */
  function localFor(cc) { return FONT_BY[CC_LOCAL[String(cc || '').toUpperCase()]] || null; }
  /* the country's own name in its own language, short form (日本, 한국,
     台灣, 香港), from the browser's locale data; '' when it has none */
  function localCountry(cc, L) {
    try {
      var c = String(cc || '').toUpperCase(), n = new Intl.DisplayNames([L.lang], { type: 'region', style: 'short' }).of(c);
      return n && n !== c && L.test.test(n) ? n : '';
    } catch (e) { return ''; }
  }
  /* a geocoder's name trimmed the way a poster letters it: a Chinese city
     without 市 (北京市 → 北京), a Japanese ward, city or prefecture without
     its office (渋谷区 → 渋谷, 大阪市 → 大阪, 東京都 → 東京), a Korean city
     without its (서울특별시 → 서울, 부산광역시 → 부산) — the Japanese and
     Korean only where the geocoder names the office itself (adm = true; a
     WOF locality is the bare name already: 四日市 stays). '' when it isn't
     in the script. */
  function localName(name, L, adm) {
    var n = String(name || '').trim();
    if (!n || !L.test.test(n)) return '';
    if ((L.key === 'zh-Hans' || L.key === 'zh-Hant') && n.length >= 3) n = n.replace(/市$/, '');
    if (L.key === 'ja' && adm && n.length >= 3) n = n.replace(/[市区都府県]$/, '');
    if (L.key === 'ko' && adm) {
      var bare = n.replace(/(특별자치시|특별시|광역시|시)$/, '');
      if (bare.length >= 2) n = bare;
    }
    return n;
  }

  var LAYOUTS = [
    { key: 'band', label: 'Band' },
    { key: 'headline', label: 'Headline' },
    { key: 'classic', label: 'Classic' },
    { key: 'lines', label: 'Lines' },
    { key: 'circle', label: 'Circle' },
    { key: 'plain', label: 'Map only' }
  ];

  function themeOf(d) { return (d && d.themeObj) || themeByKey(d && d.theme) || THEMES[0]; }
  function fontOf(d) { return FONT_BY[d && d.font] || FONTS[0]; }

  /* ── a whole state ─────────────────────────────────────────────────────
     A design with d.state (a Census two-letter code) is EVERY ROAD IN THAT
     STATE at the state ladder (2026-10-25 design: line weights in print
     millimetres, the street web as a hairline in a share of the road ink,
     so a city pools to a tone instead of to solid ink). The planet tiles
     carry no small roads at a state's scale, so the map layer is rendered
     on Kevin's PC from the WorldRoads2026 road data (docs/poster-maker/
     state/staterender.py): the maker shows a pre-rendered CLASS layer per
     state, orientation and line class, recolored into the design's colours
     right here (stateRecolor — the way the Style cards are already made for
     a city), and the print tool has the renderer draw the order's layer in
     the set's own inks. Both lay the state into the same box (stateBox), so
     the preview and the print agree. The poster is the SILHOUETTE form
     (Kevin, 2026-10-25: "Go with the silhouette form (Option A)"): the state
     cut to its outline on the paper, its border a hairline, no fade band;
     the window form stays on the design canvas as the road not taken.
       d.share  the street web's share of the road ink (0.65; Black & White's
                own ease keeps it at 0.55 — stateShare)
       d.ladder the ladder the layer was drawn at, so an old order's print
                link still says which */
  var STATE_LADDER = '2026-10-25';
  var STATE_SHARE = 0.65;
  /* the box the state is fitted into (design units): inside the map's
     rectangle with a little air (a little more where the map runs to the
     sheet's edge, g.inset); where the words sit inside that rectangle
     (Classic, Lines) the state stays above them, and below them where they
     sit at the top (Headline); in a circle, the square inside it */
  function stateBox(g) {
    var pad = 0.035 * g.S + (g.inset || 0), m = g.map;
    var x0 = m.x + pad, x1 = m.x + m.w - pad, y0 = m.y + pad, y1 = m.y + m.h - pad;
    if (g.shape === 'circle') {
      var side = m.w / Math.SQRT2 - pad;
      x0 = m.x + (m.w - side) / 2; x1 = x0 + side;
      y0 = m.y + (m.h - side) / 2; y1 = y0 + side;
    } else if (g.wordsTop) {
      y0 = g.text.tag.y + 0.025 * g.H;
    } else if (g.text && m.y + m.h > g.text.title.y) {
      y1 = (g.landscape ? 0.715 : 0.755) * g.H;
    }
    return { x0: x0, y0: y0, x1: x1, y1: y1 };
  }
  /* where the state sits (design units): fitted into its box, centred;
     aspect = its projected width over its height */
  function stateFit(g, aspect) {
    var b = stateBox(g), bw = b.x1 - b.x0, bh = b.y1 - b.y0;
    var w = Math.min(bw, bh * aspect), h = w / aspect;
    return { x: (b.x0 + b.x1) / 2 - w / 2, y: (b.y0 + b.y1) / 2 - h / 2, w: w, h: h };
  }
  /* a rendered layer's place on the poster (design units): its own state
     rectangle (meta.state_px, image px) laid onto the fitted one, uniformly.
     k = design units per image px. */
  function statePlace(g, meta) {
    var sp = meta.state_px, fit = stateFit(g, sp.w / sp.h), k = fit.w / sp.w;
    return { x: fit.x - sp.left * k, y: fit.y - sp.top * k, w: meta.image_in.px_w * k, h: meta.image_in.px_h * k, k: k, fit: fit };
  }
  /* the line class a colour set's layer is drawn in: light lines reversed
     out of a dark ground are 0.075 mm (ink spread closes finer knockout
     lines), dark on light 0.05 */
  function stateClass(t) { return lum(t.road) > lum(t.land) ? 'dark' : 'light'; }
  /* the street web's share of the road ink in a set: the maker's eased
     street ink as an alpha, capped at the design's share — the renderer's
     rule, word for word (Blue's 90 % would pool Chicago solid) */
  function stateShare(t, share) { return Math.min(1 - easeFor(t, 140, 0.1, 0.55), share || STATE_SHARE); }
  /* src = ImageData of a class layer: land red, water green, the street web
     blue at the share it was baked at (o.baked), main roads and the state
     line black — so red+green+blue is what main-road ink left, blue's part
     of it the web's coverage, red's part of red+green the land. mask =
     ImageData of the state (grey) or null. Returns ImageData in the set's
     inks, the paper outside the state left transparent. A different share
     is applied as n overlapping hairlines (1 - (1 - a)^n) in the other
     alpha. */
  function stateRecolor(src, mask, t, o, out) {
    o = o || {};
    var baked = o.baked || STATE_SHARE;
    var k = Math.log(1 - stateShare(t, o.share)) / Math.log(1 - baked);
    var L = hexRgb(t.land), Wt = hexRgb(t.water), R = hexRgb(t.road), Hn = hexRgb(t.minor || t.road);
    var a = src.data, mk = mask && mask.data, b = out.data;
    for (var i = 0; i < a.length; i += 4) {
      var r = a[i], gc = a[i + 1], bl = a[i + 2], s = r + gc + bl;
      var m = Math.max(0, 1 - s / 255), hh = s > 0 ? bl / s : 0, l = r + gc > 0 ? r / (r + gc) : 1;
      if (k !== 1 && hh > 0) hh = 1 - Math.pow(1 - hh, k);
      var inside = mk ? mk[i] / 255 : 1;
      var g0 = (1 - m) * (1 - hh), h0 = (1 - m) * hh, w = 1 - l;
      b[i] = R[0] * m + Hn[0] * h0 + g0 * (L[0] * l + Wt[0] * w);
      b[i + 1] = R[1] * m + Hn[1] * h0 + g0 * (L[1] * l + Wt[1] * w);
      b[i + 2] = R[2] * m + Hn[2] * h0 + g0 * (L[2] * l + Wt[2] * w);
      b[i + 3] = Math.round(255 * inside);
    }
    return out;
  }

  /* '13x19' → {w,h} inches, short side first; orientation swaps them */
  function sizeInches(size, orient) {
    var m = String(size || '').match(/(\d+(?:\.\d+)?)\s*[x×X]\s*(\d+(?:\.\d+)?)/);
    var a = m ? parseFloat(m[1]) : 13, b = m ? parseFloat(m[2]) : 19;
    var s = Math.min(a, b), l = Math.max(a, b);
    return orient === 'landscape' ? { w: l, h: s } : { w: s, h: l };
  }

  /* ── layout geometry (design units) ──────────────────────────────────── */
  function geom(d) {
    var inch = sizeInches(d.size, d.orient);
    var W = Math.round(inch.w * UNITS_PER_IN), H = Math.round(inch.h * UNITS_PER_IN);
    var S = Math.min(W, H), land = W > H;
    var m = 0.04 * S;
    var g = { W: W, H: H, S: S, landscape: land, margin: m, frames: [], fade: null, rules: false,
      shape: 'rect', text: null, credit: { x: W - m, y: H - m * 0.36, size: 0.0072 * S } };
    var L = d.layout || 'classic';
    var thin = 0.0026 * S;

    if (L === 'classic' || L === 'lines') {
      /* NewYorkNewYork.tif: a fine outer rule (14 px at 600 DPI on 13",
         0.0018 of the sheet) centred 0.0429 in from the edge, then a heavy
         inner rule (50 px, 0.0064) centred at 0.0534 that the map tucks
         under */
      var outer = 0.0429 * S, inner = 0.0534 * S;
      g.frames.push({ inset: outer, lw: 0.0018 * S });
      g.frames.push({ inset: inner, lw: 0.0064 * S });
      g.map = { x: inner, y: inner, w: W - 2 * inner, h: H - 2 * inner };
      g.credit = { x: W - outer, y: H - outer * 0.4, size: 0.0068 * S };
      g.fade = land ? { y0: 0.55 * H, y1: 0.69 * H } : { y0: 0.6 * H, y1: 0.75 * H };
      if (L === 'classic') {
        g.text = land
          ? { title: { y: 0.812 * H, size: 0.085 * S }, sub: { y: 0.868 * H, size: 0.037 * S }, tag: { y: 0.912 * H, size: 0.0225 * S } }
          : { title: { y: 0.8189 * H, size: 0.1054 * S }, sub: { y: 0.8754 * H, size: 0.0454 * S }, tag: { y: 0.922 * H, size: 0.0276 * S } };
      } else {
        g.rules = true;
        g.text = land
          ? { title: { y: 0.805 * H, size: 0.082 * S, bold: true }, sub: { y: 0.865 * H, size: 0.033 * S }, tag: { y: 0.91 * H, size: 0.02 * S } }
          : { title: { y: 0.808 * H, size: 0.1 * S, bold: true }, sub: { y: 0.866 * H, size: 0.04 * S }, tag: { y: 0.909 * H, size: 0.024 * S } };
      }
    } else if (L === 'headline') {
      /* HEADLINE (2026-11-01, Kevin's Metskers ask: the Point Two Design
         prints on their shelf): the map runs to the sheet's edge with no
         frame (a hairline keyline was tried — Kevin, 2026-11-01: "throwing
         the design off"), and the words sit at the TOP, on
         the map itself — the title, one rule under it, the second line,
         the coordinates — with no fade: the streets run under the letters,
         which carry a THIN halo of the ground's color (plateIn; 0.45 of the
         full one — Kevin's pick, 2026-11-01, from full, thin and none side
         by side: none let Black & White's streets run into the letters,
         the full one cut too wide a lane) so a dense grid stays out of
         them. The coordinates are in the full ink, a size up
         and two weights heavier than the other layouts' grey line (Kevin,
         2026-11-01: "make the coords easier to read" — over the streets
         the grey went faint), and the second line takes the same weight,
         so the three lines step down by size alone, as the shelf print's
         do. The place sits in the middle of the open map
         BELOW the words (aimY), the mirror of Classic's aim. The credit
         sits a little in from the corner. */
      var key = 0.03 * S;   /* the air the state and the credit keep from the edge */
      g.map = { x: 0, y: 0, w: W, h: H };
      g.inset = key;
      g.credit = { x: W - key - 0.012 * S, y: H - key - 0.012 * S, size: 0.0068 * S };
      g.text = land
        ? { title: { y: 0.2 * H, size: 0.1 * S, rule: true }, sub: { y: 0.272 * H, size: 0.04 * S, bold: true }, tag: { y: 0.324 * H, size: 0.031 * S, ink: true, bold: true } }
        : { title: { y: 0.16 * H, size: 0.125 * S, rule: true }, sub: { y: 0.235 * H, size: 0.046 * S, bold: true }, tag: { y: 0.28 * H, size: 0.035 * S, ink: true, bold: true } };
      g.halo = 0.45;
      g.wordsTop = true;
      g.aimY = (g.text.tag.y + H - key) / 2;
    } else if (L === 'band') {
      var bandTop = land ? 0.74 * H : 0.745 * H;
      g.map = { x: m, y: m, w: W - 2 * m, h: bandTop - m };
      g.frames.push({ rect: { x: m, y: m, w: W - 2 * m, h: bandTop - m }, lw: thin });
      g.text = land
        ? { title: { y: 0.83 * H, size: 0.08 * S }, sub: { y: 0.888 * H, size: 0.035 * S }, tag: { y: 0.932 * H, size: 0.02 * S } }
        : { title: { y: 0.838 * H, size: 0.095 * S }, sub: { y: 0.889 * H, size: 0.042 * S }, tag: { y: 0.929 * H, size: 0.024 * S } };
    } else if (L === 'circle') {
      var dia = land ? 0.64 * H : 0.82 * W;
      var cy = land ? 0.39 * H : 0.385 * H;
      g.shape = 'circle';
      g.map = { x: (W - dia) / 2, y: cy - dia / 2, w: dia, h: dia };
      g.ring = { lw: thin * 1.2 };
      g.text = land
        ? { title: { y: 0.845 * H, size: 0.072 * S }, sub: { y: 0.9 * H, size: 0.032 * S }, tag: { y: 0.94 * H, size: 0.019 * S } }
        : { title: { y: 0.8 * H, size: 0.1 * S }, sub: { y: 0.857 * H, size: 0.044 * S }, tag: { y: 0.9 * H, size: 0.025 * S } };
    } else {
      g.map = { x: m, y: m, w: W - 2 * m, h: H - 2 * m };
      g.frames.push({ inset: m, lw: thin });
    }
    /* a whole state sits on the paper, cut to its outline: no fade band */
    if (d.state) g.fade = null;
    return g;
  }

  /* ── the MapLibre style ─────────────────────────────────────────────── */
  /* width × zoom, in DESIGN zoom (off = log2(oz) shifts the stops onto the
     camera's zoom). Past z12, where a frame closes in on a neighbourhood, a
     street thickens (x1.7 by z14, x2.8 by z16) so a close-up doesn't read as
     thread. Below z12 it THINS as the frame takes in more ground, the way
     Kevin's masters do: QGIS draws in millimetres, but a metro frame is an
     export of more ground shrunk onto the same sheet (his Los Angeles: 125 km
     placed at ~35%, house streets ~1.3 px at 600 DPI, freeways ~10). Held at
     ~0.9 there, the streets of a whole-metro frame clogged into a black mass
     (Los Angeles at z10.08: freeways 20-30 px, arterials 10-14). Kevin,
     2026-10-29, picked "test C" — x0.39 at z10.08 — which lands his file's
     tone (map mean 198 vs his 192) and line detail (edge density 38% vs
     40%; the old widths 25%). The stops follow 2^(0.71 (z - 12)) down from
     z12, where a one-city frame stays as it was. */
  function zw(base, k, off, stops) {
    var f = function (x) { return typeof base === 'number' ? base * x * k : ['*', base, x * k]; };
    var e = ['interpolate', ['exponential', 1.4], ['zoom']];
    off = off || 0;
    (stops || MAJOR_STOPS).forEach(function (st) { e.push(st[0] + off, f(st[1])); });
    return e;
  }
  var MAJOR_STOPS = [[8, 0.14], [9, 0.23], [10, 0.38], [11, 0.61], [12, 1], [14, 1.7], [16, 2.8], [18, 4.5]];
  /* small streets and paths thin with the rest (they eased off only to x0.9
     at z10.5 before; Kevin's test C thinned them like the main roads) */
  var MINOR_STOPS = MAJOR_STOPS;
  /* base widths in design units (1 unit = 0.254 mm on paper). Started from
     Kevin's NewYorkNewYork.tif (motorway/primary 0.79 mm, residential 0.125),
     then refined against Mapiful side by side (2026-10-24, NYC + Denver —
     "still needs some refinement"): their read is a fine black skeleton of
     main roads over an even GREY web of small streets. So the avenues and
     arterials slim down (primary 0.58 mm, secondary/tertiary 0.33, motorway
     0.71) and the small streets widen a touch (residential 0.15) but print
     in minorInk grey, not black. */
  var ROAD_BASE = ['case',
    ['has', 'is_link'], ['match', ['get', 'kind_detail'], 'motorway_link', 1.5, 'trunk_link', 1.3, 1.0],
    ['==', ['get', 'kind_detail'], 'motorway'], 2.8,
    ['==', ['get', 'kind_detail'], 'trunk'], 2.2,
    ['==', ['get', 'kind'], 'highway'], 2.8,
    ['==', ['get', 'kind_detail'], 'primary'], 2.3,
    ['in', ['get', 'kind_detail'], ['literal', ['secondary', 'tertiary']]], 1.3,
    ['==', ['get', 'kind'], 'major_road'], 1.3,
    ['in', ['get', 'kind_detail'], ['literal', ['service', 'unclassified', 'living_street', 'road']]], 0.4,
    ['==', ['get', 'kind'], 'minor_road'], 0.6,
    0.4];
  /* paths, as the master's small-roads layer draws them: path and
     pedestrian 0.1 mm, footways (and cycleways) 0.06; tracks and bridleways
     are not in the master and only scribble a wide frame */
  var PATH_BASE = ['match', ['get', 'kind_detail'], ['path', 'pedestrian'], 0.4, 0.28];
  /* LINES 'bold' (Kevin's QGIS widths fitted to his 2025 Chicago poster:
     every street at full road ink, square ends) was RETIRED 2026-10-25 —
     Kevin: "we can get rid of bold mode they dont make sense anyway". A
     saved design that still says bold draws these Fine streets. */
  /* left out: tracks and bridleways scribble a wide frame; sidewalks along
     every street and crossings at every corner (in the z14 tiles a
     whole-city frame draws from) doubled each street; corridors are indoors */
  var PATH_SKIP = ['track', 'bridleway', 'sidewalk', 'crossing', 'corridor'];
  /* the planet files these as kind 'other', but they are streets: shared
     'living streets' (most of the lanes in Paris's and Prague's old towns)
     and alleys draw with the small streets, not as paths */
  var STREETLIKE = ['living_street', 'alley'];
  var PARK_KINDS = ['park', 'cemetery', 'golf_course', 'nature_reserve', 'national_park', 'protected_area',
    'forest', 'wood', 'garden', 'grass', 'grassland', 'village_green', 'playground', 'pitch',
    'recreation_ground', 'scrub', 'allotments', 'zoo'];

  /* the overzoom factor (see DETAIL in the header). A whole-city frame
     draws from the z14 tiles: the planet's z13 tiles list every residential
     street but thin out short segments, and an old town lost most of its
     streets to that (Paris's Marais: 13 residential ways in a z13 view, 294
     in a quarter of that area at z14 — Kevin, 2026-10-25, "short streets
     appearing like broken spaghetti", fixed the same day). z14 brings
     sidewalks, crossings and corridors too, which PATH_SKIP leaves out.
     Cost of a whole 13x19 at z14: Paris 151 tile requests, 8.3 MB (z13: 40,
     3.2); New York's opening frame 533, 12.7 MB (z13: 151, 4.5). Frames
     wider than z11 stay at x8 (z13 tiles at z10). PHONES keep the depth
     they had (ozForPhone, x4 at most): a phone's whole-poster view can't
     show the gaps and a few hundred more tiles is a lot of GPU memory.
     The print file takes ozFor. o.oz or o.detail (log2) override.
     History: z12 tiles carry every residential street but no service
     roads, alleys, footways or paths; z13 adds them (a Denver suburb: 532 km
     of line becomes 1,062 km, Kevin's QGIS master's ink coverage). */
  function ozFor(zoom) { return zoom >= 13 ? 2 : zoom >= 12 ? 4 : 8; }
  function ozForPhone(zoom) { return Math.min(4, zoom >= 12 ? 2 : zoom >= 11 ? 4 : 8); }
  /* THE WHOLE POSTER ON A DESKTOP SCREEN (2026-10-29, the maker's view
     where an every-building archive covers the place): one level under the
     print's depth, as long as that still reaches the z13 tiles, which carry
     every street, service road and path. Measured as a phone (390 px at 3x)
     and a desktop (1440 px at 1x and 2x) show the whole poster, Black &
     White, against the print's depth with every building: New York, Paris,
     Prague and Denver one level lighter came out 0.3 to 1.0 grey levels (of
     255) lighter, a mean pixel difference of 1.1 to 1.6 after a 3 px blur
     (New York: 222 tile requests and 17 MB instead of 861 and 28). The z12
     tiles came out 10 to 25 levels lighter (no service roads or paths), so
     a wide frame (Los Angeles at 10.1) keeps the print's z13. Without the
     archive the planet's own small buildings, only in its z14 tiles, are
     the texture of every block (Tokyo one level lighter: 6 levels), so the
     maker keeps the print's depth there. */
  function ozForView(zoom) {
    var full = ozFor(zoom);
    return Math.min(full, Math.max(full / 2, Math.pow(2, Math.ceil(13 - zoom))));
  }
  function ozOf(o, d) {
    if (o && o.oz) return o.oz;
    if (o && o.detail != null) return Math.pow(2, o.detail);
    return ozFor(d && isFinite(d.zoom) ? d.zoom : 12);
  }
  /* the camera zoom of a map laid out oz times its design size */
  function camZoom(d, oz) { return d.zoom + Math.log(oz) / Math.LN2; }

  function style(d, o) {
    o = o || {};
    var t = o.theme || themeOf(d);
    var oz = ozOf(o, d), off = Math.log(oz) / Math.LN2;
    var roadBase = ROAD_BASE, pathBase = PATH_BASE, minorStops = MINOR_STOPS, majorStops = MAJOR_STOPS;
    var ends = { 'line-cap': 'round', 'line-join': 'round' };
    var k = (o.widthScale || 1) * oz;
    var url = o.pmtilesUrl || DEFAULT_PMTILES;
    /* tunnels draw like any road: Kevin's QGIS data has no tunnel split, and
       leaving them out broke roads mid-city (Chicago's Eisenhower stopped
       short of the Loop where it runs under the Old Post Office) */
    var roads = ['!=', ['get', 'kind_detail'], 'pier'];
    var streetlike = ['all', ['==', ['get', 'kind'], 'other'], ['in', ['get', 'kind_detail'], ['literal', STREETLIKE]]];
    var layers = [
      { id: 'bg', type: 'background', paint: { 'background-color': t.water } },
      { id: 'earth', type: 'fill', source: 'pm', 'source-layer': 'earth', paint: { 'fill-color': t.land } },
      { id: 'park', type: 'fill', source: 'pm', 'source-layer': 'landuse',
        filter: ['in', ['get', 'kind'], ['literal', PARK_KINDS]], paint: { 'fill-color': t.park } },
      { id: 'water', type: 'fill', source: 'pm', 'source-layer': 'water',
        filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': t.water } },
      { id: 'water-line', type: 'line', source: 'pm', 'source-layer': 'water',
        filter: ['all', ['==', ['geometry-type'], 'LineString'], ['in', ['get', 'kind'], ['literal', ['river', 'canal']]]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': t.water, 'line-width': zw(['match', ['get', 'kind'], 'river', 1.4, 1.0], k, off, MAJOR_STOPS) } },
      { id: 'pier', type: 'fill', source: 'pm', 'source-layer': 'landuse',
        filter: ['==', ['get', 'kind'], 'pier'], paint: { 'fill-color': t.land } }
    ];
    /* woods (Trailhead): forest and wood over the park fill, a step deeper */
    if (t.woods) layers.splice(3, 0, { id: 'wood', type: 'fill', source: 'pm', 'source-layer': 'landuse',
      filter: ['in', ['get', 'kind'], ['literal', ['forest', 'wood']]], paint: { 'fill-color': t.woods } });
    /* SHORE (Atlas): a thin line in its own ink along every water edge, and
       along the rivers, canals and streams drawn as lines, over their band */
    if (t.shore) layers.push(
      { id: 'shore', type: 'line', source: 'pm', 'source-layer': 'water',
        filter: ['==', ['geometry-type'], 'Polygon'], layout: ends,
        paint: { 'line-color': t.shore, 'line-width': zw(0.3, k, off, minorStops) } },
      { id: 'shore-line', type: 'line', source: 'pm', 'source-layer': 'water',
        filter: ['all', ['==', ['geometry-type'], 'LineString'], ['in', ['get', 'kind'], ['literal', ['river', 'canal', 'stream']]]],
        layout: ends,
        paint: { 'line-color': t.shore, 'line-width': zw(0.3, k, off, minorStops) } });
    /* BUILDINGS. The planet only has small buildings (houses) in its z15
       tiles, so on its own a whole-city frame shows the large ones. Kevin's
       masters draw EVERY footprint — the grey texture in every block — so
       when o.buildingsUrl names a buildings archive (build_buildings.py:
       every OSM footprint at z12–z14, layer 'buildings') it draws them from
       z12 up, on top of the planet's own.
       (A landuse "built-up" shading stood in on 2026-10-24 and read as mud.)
       o.buildingsUrl may name SEVERAL archives (an array, or one string
       with commas) — one per continent (build_band.py + merge_buildings.py);
       each is its own source, and MapLibre only asks an archive for tiles
       inside its bounds. */
    var bldUrls = (Array.isArray(o.buildingsUrl) ? o.buildingsUrl : String(o.buildingsUrl || '').split(','))
      .map(function (s) { return String(s).trim(); }).filter(Boolean);
    /* no antialiased edge on footprints: a house is 1-2 px at a city frame,
       and the soft edge MapLibre draws around each fill nearly doubled it —
       blocks printed 34% grey where Kevin's Chicago poster has 23% (his
       2400 DPI export, shrunk to 600, keeps the true area) */
    /* the set's own house tone at every zoom, as Kevin's masters print
       them. From 2026-10-25 (Prague's roads "hard to see": an old town's
       footprints, a pixel or two across, merged with the small streets into
       one grey mass) to 2026-10-29 they were drawn halfway back to the land
       below design zoom 13, which left them all but invisible at a city
       frame (2-9 L* off the land) in every set but Black & White. The small
       streets keep their distance through the house tones now (THEMES:
       no more than 0.6 of the way to them). */
    var bldPaint = { 'fill-color': t.building, 'fill-antialias': false };
    if (o.buildings != null ? o.buildings : d.buildings) {
      var planetBld = { id: 'building', type: 'fill', source: 'pm', 'source-layer': 'buildings',
        filter: ['in', ['get', 'kind'], ['literal', ['building', 'building_part']]],
        paint: bldPaint };
      /* both: an archive adds the houses where it has coverage, the
         planet keeps the large buildings everywhere else — the same
         opaque fill twice is the same pixels */
      /* LANDMARK (Atlas): the ochre goes to the palace, the station and
         the stadium, never the houses. The tiles carry no size or name, but
         the planet keeps only its biggest buildings in a z12 tile, so a
         second source on the same archive capped at z12 serves those at
         every frame, overzoomed, drawn OVER the archive's houses (a z13 cap
         turned every Haussmann block ochre) */
      if (t.landmark) {
        planetBld.source = 'pmlow';
        planetBld.paint = { 'fill-color': t.landmark, 'fill-antialias': false };
      } else layers.push(planetBld);
      /* o.buildingsFrom (a DESIGN zoom): the archives start there instead of
         at their first zoom, fading in over the last 0.2 of a zoom so the
         step isn't a pop. The maker passes it on phones only; MapLibre asks
         a source for no tiles while every layer using it is out of range,
         so below it a phone downloads none of them */
      var from = o.buildingsFrom != null ? o.buildingsFrom + off : null;
      var archivePaint = from == null ? bldPaint
        : Object.assign({}, bldPaint, { 'fill-opacity': ['interpolate', ['linear'], ['zoom'], from - 0.2, 0, from, 1] });
      var archiveMin = from == null ? 12 : Math.max(12, from - 0.2);
      bldUrls.forEach(function (u, i) {
        layers.push({ id: 'building-all' + (i ? '-' + i : ''), type: 'fill', source: 'bld' + (i || ''), 'source-layer': 'buildings',
          minzoom: archiveMin, paint: archivePaint });
      });
      if (t.landmark) layers.push(planetBld);
    }
    /* elevation: over the buildings, under every line. o.elevation false
       leaves out the hills, the elevation lines and the summits (the
       maker's map while its streets load) */
    var R = Object.assign({}, RELIEF, o.reliefOpts || {});
    var elev = o.elevation !== false && !(d && d.state);
    var relief = (o.relief != null ? !!o.relief : !!(d && d.relief)) && elev;
    if (relief) layers.push({ id: 'relief', type: 'hillshade', source: 'dem', paint: reliefPaint(t, R) });
    /* contour lines (Trailhead): over the shading, under every line */
    var terrainUrl = o.terrainUrl || DEFAULT_TERRAIN;
    var contours = !!t.contours && elev && ensureDemProtocol();
    var summits = !!t.summits && elev && ensureDemProtocol();
    if (contours) layers.push(
      { id: 'contour', type: 'line', source: 'contours', filter: ['==', ['get', 'index'], 0],
        layout: ends, paint: { 'line-color': t.ink, 'line-opacity': 0.28, 'line-width': zw(0.22, k, off, minorStops) } },
      { id: 'contour-index', type: 'line', source: 'contours', filter: ['==', ['get', 'index'], 1],
        layout: ends, paint: { 'line-color': t.ink, 'line-opacity': 0.55, 'line-width': zw(0.46, k, off, minorStops) } });
    var pathFilter = ['all', roads, ['in', ['get', 'kind'], ['literal', ['path', 'other']]],
      ['!', ['in', ['get', 'kind_detail'], ['literal', PATH_SKIP]]], ['!', streetlike]];
    if (t.trails) {
      /* TRAILS (Trailhead). What the data calls a path is a hiking or
         mountain-bike trail; a track is a forest or farm road; a sidewalk
         through a park, a path between buildings or a bike lane is a
         footway or cycleway. The trails go in the coordinates ink, dashed
         and heavier (dashes in line widths, butt ends so the gaps stay);
         footways keep a faint path-ink dash; steps are short ticks in the
         small-street ink; pedestrian streets stay solid streets. */
      var dashEnds = { 'line-cap': 'butt', 'line-join': 'round' };
      layers.push(
        { id: 'path', type: 'line', source: 'pm', 'source-layer': 'roads',
          filter: ['all', pathFilter, ['!', ['in', ['get', 'kind_detail'], ['literal', ['pedestrian', 'steps', 'path']]]]],
          layout: dashEnds,
          paint: { 'line-color': pathInk(t), 'line-width': zw(0.42, k, off, minorStops), 'line-dasharray': [2.6, 2] } },
        { id: 'path-steps', type: 'line', source: 'pm', 'source-layer': 'roads',
          filter: ['all', pathFilter, ['==', ['get', 'kind_detail'], 'steps']],
          layout: dashEnds,
          paint: { 'line-color': minorInk(t), 'line-width': zw(0.6, k, off, minorStops), 'line-dasharray': [0.6, 0.9] } },
        { id: 'path-pedestrian', type: 'line', source: 'pm', 'source-layer': 'roads',
          filter: ['all', pathFilter, ['==', ['get', 'kind_detail'], 'pedestrian']],
          layout: ends,
          paint: { 'line-color': minorInk(t), 'line-width': zw(pathBase, k, off, minorStops) } },
        /* THE WIDE-FRAME LADDER (Kevin, 2026-10-27: zoomed out "the dashed
           trails start to look like this" — a scribble; the "Trails at a
           wide frame" board). Tracks, most of the scribble, draw from
           design zoom 12 up. Trails are full from 13, easing to 60 % and
           0.8 of their width by 11, and off below 10.5, where one trail is
           under a pixel anyway; while thin they take a longer dash and a
           shorter gap so they still read as lines. The ease is folded into
           the width's own stops: MapLibre refuses a zoom rule nested inside
           a multiply (silently — the map never settles). Named-only trails
           at wide frames were tried and dropped: too few carry a name. */
        { id: 'trail-track', type: 'line', source: 'pm', 'source-layer': 'roads', minzoom: 12 + off,
          filter: ['all', roads, ['in', ['get', 'kind'], ['literal', ['path', 'other']]], ['in', ['get', 'kind_detail'], ['literal', ['track', 'bridleway']]]],
          layout: dashEnds,
          paint: { 'line-color': t.tag, 'line-width': zw(0.65, k, off, minorStops), 'line-dasharray': [4, 2, 1, 2] } },
        { id: 'trail', type: 'line', source: 'pm', 'source-layer': 'roads', minzoom: 10.5 + off,
          filter: ['all', pathFilter, ['==', ['get', 'kind_detail'], 'path']],
          layout: dashEnds,
          paint: { 'line-color': t.tag, 'line-width': trailWidth(0.85, k, off, minorStops),
            'line-opacity': ['interpolate', ['linear'], ['zoom'], 10.5 + off, 0, 11 + off, 0.6, 13 + off, 1],
            'line-dasharray': ['step', ['zoom'], ['literal', [3.6, 1.5]], 13 + off, ['literal', [2.2, 1.4]]] } });
    } else layers.push(
      { id: 'path', type: 'line', source: 'pm', 'source-layer': 'roads',
        filter: pathFilter,
        layout: ends,
        /* a pedestrian street is a street (Prague's old town is mostly
           pedestrian lanes): small-street ink; footways and the rest: path ink */
        paint: { 'line-color': ['match', ['get', 'kind_detail'], 'pedestrian', minorInk(t), pathInk(t)],
          'line-width': zw(pathBase, k, off, minorStops) } });
    /* RAIL (Atlas): mainline railways only, a grey dash under the streets;
       subways and trams stay out */
    if (t.rail) layers.push(
      { id: 'rail', type: 'line', source: 'pm', 'source-layer': 'roads',
        filter: ['all', ['==', ['get', 'kind'], 'rail'], ['==', ['get', 'kind_detail'], 'rail']],
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: { 'line-color': t.rail, 'line-width': zw(0.5, k, off, minorStops), 'line-dasharray': [3, 2] } });
    layers.push(
      { id: 'road-minor', type: 'line', source: 'pm', 'source-layer': 'roads',
        filter: ['all', roads, ['any', ['==', ['get', 'kind'], 'minor_road'], streetlike]],
        layout: ends,
        paint: { 'line-color': minorInk(t), 'line-width': zw(roadBase, k, off, minorStops) } },
      { id: 'road', type: 'line', source: 'pm', 'source-layer': 'roads',
        filter: ['all', roads, ['in', ['get', 'kind'], ['literal', ['highway', 'major_road']]]],
        layout: Object.assign({ 'line-sort-key': ['coalesce', ['get', 'sort_rank'], 0] }, ends),
        paint: { 'line-color': t.road, 'line-width': zw(roadBase, k, off, majorStops) } }
    );
    /* BOUNDS (Atlas): county and locality lines, dash-dot in slate, over the
       roads; region and country lines stay out */
    if (t.bounds) layers.push(
      { id: 'bounds', type: 'line', source: 'pm', 'source-layer': 'boundaries',
        filter: ['in', ['get', 'kind'], ['literal', ['county', 'locality']]],
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: { 'line-color': t.bounds, 'line-width': zw(0.9, k, off, minorStops), 'line-dasharray': [4, 1.6, 1, 1.6] } });
    ensureArchive(url);
    var sources = { pm: { type: 'vector', url: 'pmtiles://' + url } };
    if (t.landmark) sources.pmlow = { type: 'vector', url: 'pmtiles://' + url, maxzoom: 12 };
    bldUrls.forEach(function (u, i) {
      ensureArchive(u);
      sources['bld' + (i || '')] = { type: 'vector', url: 'pmtiles://' + u };
    });
    if (relief) sources.dem = reliefSource(o, oz, R);
    /* the elevation lines and summits (Trailhead): GeoJSON the krdem://
       protocol computes from the DEM for this design's window */
    if (contours) sources.contours = { type: 'geojson', data: demUrl('contours', d, terrainUrl, o.demDrop) };
    if (summits) {
      sources.summits = { type: 'geojson', data: demUrl('summits', d, terrainUrl, o.demDrop) };
      layers.push(
        { id: 'summit-halo', type: 'fill', source: 'summits', filter: ['==', ['get', 'halo'], 1], paint: { 'fill-color': t.paper } },
        { id: 'summit', type: 'fill', source: 'summits', filter: ['==', ['get', 'halo'], 0], paint: { 'fill-color': t.tag } });
    }
    return { version: 8, sources: sources, layers: layers };
  }

  /* ── elevation's look ───────────────────────────────────────────────── */
  /* a light sheet shades in its own darkest ink (one ink, like its lines)
     and lifts its lit slopes toward white; a dark sheet shades toward black
     and lights its slopes in the street ink. Lit from the north-west, as a
     relief map is, fixed to the map (the poster is north-up). */
  function reliefPaint(t, R) {
    var dark = lum(t.land || t.paper) < 128;
    var inks = [t.road, t.minor, t.ink].filter(Boolean).sort(function (a, b) { return lum(a) - lum(b); });
    var sh = dark ? '#000000' : inks[0], hi = dark ? t.road : '#ffffff';
    return {
      'hillshade-method': R.method,
      'hillshade-exaggeration': R.exaggeration,
      'hillshade-shadow-color': rgba(sh, dark ? R.darkShadow : R.shadow),
      'hillshade-highlight-color': rgba(hi, dark ? R.darkHighlight : R.highlight),
      'hillshade-accent-color': rgba(sh, R.accent),
      'hillshade-illumination-direction': R.direction,
      'hillshade-illumination-anchor': 'map'
    };
  }
  /* the DEM, pinned to the DESIGN zoom (+ R.depth): a raster source fetches
     log2(tileSize/512) zooms shallower as tileSize grows, so 512 x oz
     cancels the overzoom (the travel books' trick). Every screen and the
     print shade from the same DEM tiles, so the hills read the same on
     each, and a whole city costs a dozen tiles */
  function reliefSource(o, oz, R) {
    var url = o.terrainUrl || DEFAULT_TERRAIN;
    ensureArchive(url);
    return { type: 'raster-dem', url: 'pmtiles://' + url, encoding: 'terrarium', tileSize: 512 * oz / Math.pow(2, R.depth || 0) };
  }
  /* the shading alone and neutral (black shadow, white light on mid grey),
     for the style cards: one render laid over every recolored card with a
     soft-light blend. o.oz pins the DEM as above for the card's camera. */
  function reliefStyle(d, o) {
    o = o || {};
    var R = Object.assign({}, RELIEF, o.reliefOpts || {});
    return { version: 8, sources: { dem: reliefSource(o, o.oz || 1, R) }, layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': '#808080' } },
      { id: 'relief', type: 'hillshade', source: 'dem', paint: {
        'hillshade-method': R.method, 'hillshade-exaggeration': R.exaggeration,
        'hillshade-shadow-color': 'rgba(0,0,0,' + R.shadow + ')',
        'hillshade-highlight-color': 'rgba(255,255,255,' + R.highlight + ')',
        'hillshade-accent-color': 'rgba(0,0,0,' + R.accent + ')',
        'hillshade-illumination-direction': R.direction, 'hillshade-illumination-anchor': 'map' } }
    ] };
  }

  /* ── frame, fade and words ──────────────────────────────────────────── */
  /* a mix of two #rrggbb colors, t of the way from a to b */
  function mixHex(a, b, t) {
    var x = hexRgb(a), y = hexRgb(b);
    return '#' + [0, 1, 2].map(function (i) {
      return ('0' + Math.round(x[i] + (y[i] - x[i]) * t).toString(16)).slice(-2);
    }).join('');
  }
  /* the small streets' and paths' ink: the road color eased toward the
     land, by as much as the theme's own contrast allows. A flat 55% mix
     (Mapiful's B&W residential grey) left the small streets only a fifth
     of the land-to-road range away from the buildings in EVERY theme, and
     with every building drawn, dense centers turned to grey mush (Kevin,
     2026-10-25, Prague in Blue beside Mapiful's: "roads in cities like
     prague they are hard to see on fine streets mode"). Now a small street
     keeps ~140 of 255 luminance levels off the land and a path ~100: B&W
     eases 45% (grey, Mapiful's read), Blue 10% (near white, as on Kevin's
     own Blue Prague master), every theme by its own contrast. */
  function lum(hex) { var c = hexRgb(hex); return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]; }
  function easeFor(t, levels, lo, hi) {
    var range = Math.abs(lum(t.road) - lum(t.land));
    return Math.max(lo, Math.min(hi, 1 - levels / Math.max(1, range)));
  }
  /* zw() with the trails' ease folded in: 0.8 of the width at design zoom
     11 rising to the full width at 13 (one interpolate, see the ladder) */
  function trailWidth(base, k, off, stops) {
    var e = ['interpolate', ['exponential', 1.4], ['zoom']];
    stops.forEach(function (st) {
      var f = Math.max(0.8, Math.min(1, 0.8 + 0.2 * (st[0] - 11) / 2));
      e.push(st[0] + off, base * st[1] * k * f);
    });
    return e;
  }
  function minorInk(t) { return t.minor || mixHex(t.road, t.land, easeFor(t, 140, 0.1, 0.55)); }
  function pathInk(t) { return t.pathInk || mixHex(t.road, t.land, easeFor(t, 100, 0.3, 0.7)); }

  function rgba(hex, a) {
    var h = String(hex).replace('#', '');
    if (h.length === 3) h = h.replace(/./g, function (c) { return c + c; });
    var n = parseInt(h, 16);
    return 'rgba(' + (n >> 16 & 255) + ',' + (n >> 8 & 255) + ',' + (n & 255) + ',' + a + ')';
  }
  var _canSpace = null;
  function canLetterSpace(ctx) {
    if (_canSpace === null) _canSpace = ('letterSpacing' in ctx);
    return _canSpace;
  }
  /* spec.fallback: a second face for the letters the first lacks (a local
     lettering's coordinates, words typed in a script under a Latin face) */
  function fontCss(spec, px) {
    return (spec.italic ? 'italic ' : '') + spec.weight + ' ' + px.toFixed(2) + 'px "' + spec.family + '"' +
      (spec.fallback && spec.fallback !== spec.family ? ', "' + spec.fallback + '"' : '');
  }
  /* the local lettering a line's words are written in — the design's own
     (d.local's country) first, so 東京 under a Japanese place is Japanese */
  function scriptOf(text, d) {
    var s = String(text || ''), own = d && d.local && localFor(d.local.cc);
    if (!s || /^[\x00-\x7f]*$/.test(s)) return null;
    if (own && own.test.test(s)) return own;
    for (var i = 0; i < LOCAL.length; i++) if (LOCAL[i].test.test(s)) return LOCAL[i];
    return null;
  }
  /* the spec a line is drawn in: the lettering's own, with the script's
     face behind it when the words are in a script the lettering's face
     doesn't carry (a Japanese title typed under Classic prints in Noto
     Sans JP, not in whatever the device falls back to) */
  function lineSpec(f, key, text, d) {
    var spec = f[key];
    if (spec.fallback) return spec;
    var L = f.local ? null : scriptOf(text, d);
    return L ? Object.assign({}, spec, { fallback: L.family, lang: L.lang }) : spec;
  }
  /* the letter spacing a line gets at px. A canvas without letterSpacing
     spaces letter by letter, in the order they're typed — which would turn
     Hebrew around and pull Thai marks off their letters — so those go
     unspaced there */
  var LETTER_BY_LETTER_UNSAFE = /[\p{sc=Hebrew}\p{sc=Arabic}\p{sc=Thai}\p{sc=Khmer}\p{sc=Lao}\p{sc=Myanmar}]/u;
  function spaceOf(ctx, text, spec, px) {
    return !canLetterSpace(ctx) && LETTER_BY_LETTER_UNSAFE.test(text) ? 0 : spec.track * px;
  }
  /* width of a line set in spec at px — letter spacing counted between
     letters only (the canvas adds it after the last one too) */
  function measure(ctx, text, spec, px) {
    var sp = spaceOf(ctx, text, spec, px);
    ctx.font = fontCss(spec, px);
    if (!sp) { setSpace(ctx, 0); return ctx.measureText(text).width; }
    if (canLetterSpace(ctx)) { setSpace(ctx, sp); return ctx.measureText(text).width - sp; }
    var w = 0, chars = Array.from(text);
    for (var i = 0; i < chars.length; i++) w += ctx.measureText(chars[i]).width;
    return w + sp * Math.max(0, chars.length - 1);
  }
  function setSpace(ctx, sp) { if (canLetterSpace(ctx)) ctx.letterSpacing = sp.toFixed(2) + 'px'; }
  /* halo = { color, lw }: a stroke of the ground's color behind the
     letters first (Headline, whose words sit on the streets), the way a
     map's labels are knocked out of what runs under them */
  function drawLine(ctx, text, spec, px, cx, y, halo) {
    var w = measure(ctx, text, spec, px);
    var sp = spaceOf(ctx, text, spec, px);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    var whole = !sp || canLetterSpace(ctx), x0 = cx - w / 2, chars = whole ? null : Array.from(text);
    function run(put) {
      if (whole) { put(text, x0); return; }
      var x = x0;
      for (var i = 0; i < chars.length; i++) { put(chars[i], x); x += ctx.measureText(chars[i]).width + sp; }
    }
    if (halo) {
      ctx.save();
      ctx.strokeStyle = halo.color; ctx.lineWidth = halo.lw; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      run(function (s, x) { ctx.strokeText(s, x, y); });
      ctx.restore();
    }
    run(function (s, x) { ctx.fillText(s, x, y); });
    return w;
  }
  function caseOf(text, spec) { return spec.caps ? String(text).toLocaleUpperCase(spec.lang || undefined) : String(text); }
  /* the spacing that sets text at target width, kept within the spec's
     minTrack..maxTrack (a long name stops at minTrack and shrinks later) */
  function fitTrack(ctx, text, spec, px, target) {
    var lo = spec.minTrack != null ? spec.minTrack : spec.track, hi = spec.maxTrack != null ? spec.maxTrack : spec.track;
    var at = function (tr) { return measure(ctx, text, Object.assign({}, spec, { track: tr }), px); };
    if (at(lo) >= target) return Object.assign({}, spec, { track: lo });
    if (at(hi) <= target) return Object.assign({}, spec, { track: hi });
    for (var i = 0; i < 18; i++) { var mid = (lo + hi) / 2; if (at(mid) < target) lo = mid; else hi = mid; }
    return Object.assign({}, spec, { track: (lo + hi) / 2 });
  }

  function strokeRect(ctx, x, y, w, h, lw) {
    ctx.lineWidth = lw;
    ctx.strokeRect(x, y, w, h);
  }

  /* ── the marker (Mapiful's "icons"): one shape on one spot ─────────────
     Drawn in a 24-unit box, then scaled; ax/ay is the point that sits on the
     marked spot (a pin's tip, the middle of the others). d.marker =
     { icon, lat, lon }, or nothing. */
  var MARKERS = [
    { key: 'heart', label: 'Heart', ax: 12, ay: 12.6,
      path: 'M12 21.3C10.9 20.3 4.3 14.6 3 11.5 1.7 8.3 3.5 4.7 7 4.4c2.2-.2 3.8.9 5 2.6 1.2-1.7 2.8-2.8 5-2.6 3.5.3 5.3 3.9 4 7.1-1.3 3.1-7.9 8.8-9 9.8z' },
    { key: 'pin', label: 'Pin', ax: 12, ay: 23,
      path: 'M12 23s-7.5-8.4-7.5-13.5a7.5 7.5 0 0 1 15 0C19.5 14.6 12 23 12 23zm0-10.6a2.9 2.9 0 1 0 0-5.8 2.9 2.9 0 0 0 0 5.8z' },
    { key: 'star', label: 'Star', ax: 12, ay: 12.4,
      path: 'M12 2.6l2.85 6.1 6.65.75-4.95 4.55 1.35 6.6L12 17.3l-5.9 3.3 1.35-6.6L2.5 9.45l6.65-.75z' },
    { key: 'dot', label: 'Dot', ax: 12, ay: 12,
      path: 'M12 16.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4zM12 20.6a8.6 8.6 0 1 1 0-17.2 8.6 8.6 0 0 1 0 17.2zm0-1.9a6.7 6.7 0 1 0 0-13.4 6.7 6.7 0 0 0 0 13.4z' }
  ];
  var MARKER_BY = {};
  MARKERS.forEach(function (m) { MARKER_BY[m.key] = m; });
  var _paths = {};
  function markerPath(key) {
    if (!_paths[key] && root.Path2D && MARKER_BY[key]) _paths[key] = new root.Path2D(MARKER_BY[key].path);
    return _paths[key] || null;
  }
  /* a lat/lon on the poster, in design units (bearing 0: the maker keeps
     maps north-up) */
  function posterXY(d, g, lat, lon) {
    var R = 6378137, rad = Math.PI / 180;
    var mpu = 2 * Math.PI * R / (512 * Math.pow(2, d.zoom));
    var my = function (la) { return R * Math.log(Math.tan(Math.PI / 4 + la * rad / 2)); };
    return { x: g.map.x + g.map.w / 2 + R * (lon - d.lon) * rad / mpu,
             y: g.map.y + g.map.h / 2 - (my(lat) - my(d.lat)) / mpu };
  }
  /* THE AIM: where a picked place sits on the poster. A layout whose map
     runs under the words and fades out there (Classic, Lines) puts the
     place in the middle of the OPEN map — halfway from the map's top to
     the middle of the fade — not the sheet's middle, which sits down by
     the title (Kevin, 2026-10-26: "the city center needs to be higher
     up"). Headline, its words at the top of the map, puts the place in
     the middle of the open map BELOW them (g.aimY). Every other layout
     aims at its own map's middle. The camera
     (d.lat) stays the map's middle; aimLat reads the aimed spot off it and
     camLat turns a place into the camera that aims at it. g defaults to
     the design's own geometry. A state is laid out by its own frame. */
  function aimUnits(g) {
    if (!g || !g.map) return 0;
    if (g.aimY != null) return g.map.y + g.map.h / 2 - g.aimY;
    if (!g.fade) return 0;
    return g.map.y + g.map.h / 2 - (g.map.y + (g.fade.y0 + g.fade.y1) / 2) / 2;
  }
  function aimMeters(d, g) {
    if (d.state) return 0;
    return aimUnits(g || geom(d)) * 2 * Math.PI * 6378137 / (512 * Math.pow(2, d.zoom));
  }
  function mercY(lat) { return 6378137 * Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)); }
  function mercLat(y) { return (2 * Math.atan(Math.exp(y / 6378137)) - Math.PI / 2) * 180 / Math.PI; }
  function aimLat(d, g) { var m = aimMeters(d, g); return m ? mercLat(mercY(d.lat) + m) : d.lat; }
  function camLat(d, lat, g) { var m = aimMeters(d, g); return m ? mercLat(mercY(lat) - m) : lat; }
  /* the marker: the theme's ink with a thin ring of the land color, so it
     stays a clean shape on top of a dense street grid */
  function drawMarker(ctx, d, g, t) {
    var mk = d.marker, def = mk && MARKER_BY[mk.icon], path = def && markerPath(mk.icon);
    if (!path || !isFinite(mk.lat) || !isFinite(mk.lon)) return;
    var at = posterXY(d, g, mk.lat, mk.lon);
    var size = 0.05 * g.S, sc = size / 24;
    ctx.save();
    ctx.beginPath();
    if (g.shape === 'circle') ctx.arc(g.map.x + g.map.w / 2, g.map.y + g.map.h / 2, g.map.w / 2, 0, Math.PI * 2);
    else ctx.rect(g.map.x, g.map.y, g.map.w, g.map.h);
    ctx.clip();
    ctx.translate(at.x - def.ax * sc, at.y - def.ay * sc);
    ctx.scale(sc, sc);
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = t.land;
    ctx.stroke(path);
    ctx.fillStyle = t.marker || t.ink;
    ctx.fill(path, 'evenodd');
    ctx.restore();
  }

  /* k = device px per design unit; g = geom(d) (computed when omitted) */
  /* the overlay over the map at k device px per unit: the fade and the
     marker, the PLATE (the frame lines and the words; o.noPlate leaves it
     out, for a risograph's drawPlateOff or the editor's paintPlate to draw
     off register), then the credit. o.theme, o.noText, o.noCredit. */
  function drawOverlay(ctx, d, k, g, o) {
    o = o || {};
    g = g || geom(d);
    var t = o.theme || themeOf(d);
    ctx.save();
    ctx.scale(k, k);

    /* the map dissolves into the fade band: opacity rises y0 → y1, and the
       band's own color runs fadeTop → fadeBottom down to the frame (the
       B&W master's light grey easing to white) */
    if (g.fade && g.map) {
      var mx = g.map.x, mw = g.map.w, mb = g.map.y + g.map.h;
      var top = t.fadeTop || t.paper, bot = t.fadeBottom || top;
      var fm = t.fadeMax != null ? t.fadeMax : 1;
      var r1 = Math.min(1, (g.fade.y1 - g.fade.y0) / (mb - g.fade.y0));
      var gr = ctx.createLinearGradient(0, g.fade.y0, 0, mb);
      gr.addColorStop(0, rgba(top, 0));
      gr.addColorStop(r1 * 0.5, rgba(top, fm * 0.6));
      gr.addColorStop(r1, rgba(top, fm));
      gr.addColorStop(1, rgba(bot, fm));
      ctx.fillStyle = gr;
      ctx.fillRect(mx, g.fade.y0, mw, mb - g.fade.y0);
    }

    if (d.marker && g.map) drawMarker(ctx, d, g, t);
    if (!o.noPlate) plateIn(ctx, d, g, t, o);

    if (!o.noCredit && g.credit) {
      var csz = g.credit.size;
      ctx.font = '400 ' + csz.toFixed(2) + 'px "Barlow", sans-serif';
      setSpace(ctx, 0);
      ctx.fillStyle = rgba(t.ink, 0.45);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'alphabetic';
      /* the terrain's credit rides along when the hills are drawn, as in the books */
      ctx.fillText(CREDIT + (d.relief && !d.state ? ' · Mapterhorn' : ''), g.credit.x, g.credit.y);
    }
    ctx.restore();
  }

  /* THE PLATE: the frame lines and the words, what a risograph prints off
     register. Transparent where it prints nothing. */
  function drawPlate(ctx, d, k, g, o) {
    o = o || {};
    g = g || geom(d);
    var t = o.theme || themeOf(d);
    ctx.save();
    ctx.scale(k, k);
    plateIn(ctx, d, g, t, o);
    ctx.restore();
  }
  function plateIn(ctx, d, g, t, o) {
    var f = fontOf(d);
    ctx.strokeStyle = t.frame;
    g.frames.forEach(function (fr) {
      if (fr.rect) strokeRect(ctx, fr.rect.x, fr.rect.y, fr.rect.w, fr.rect.h, fr.lw);
      else strokeRect(ctx, fr.inset, fr.inset, g.W - 2 * fr.inset, g.H - 2 * fr.inset, fr.lw);
    });
    if (g.ring) {
      ctx.lineWidth = g.ring.lw;
      ctx.beginPath();
      ctx.arc(g.map.x + g.map.w / 2, g.map.y + g.map.h / 2, g.map.w / 2, 0, Math.PI * 2);
      ctx.stroke();
    }

    if (g.text && !o.noText) {
      ctx.fillStyle = t.ink;
      var cx = g.W / 2;
      var maxW = (g.shape === 'circle' ? g.W - 4 * g.margin : g.map.w) * 0.84;
      var lines = [['title', d.title], ['sub', d.sub], ['tag', d.tag]];
      lines.forEach(function (row) {
        var key = row[0], raw = row[1];
        if (!raw || !String(raw).trim()) return;
        var spec = lineSpec(f, key, raw, d), pos = g.text[key];
        /* a line set bold by its layout (Lines' title, Headline's
           coordinates): two weights up */
        if (pos.bold) spec = Object.assign({}, spec, { weight: Math.min(800, spec.weight + 200) });
        var text = caseOf(String(raw).trim(), spec);
        var px = pos.size * (spec.scale || 1);
        if (spec.fit) spec = fitTrack(ctx, text, spec, px, spec.fit * g.W);
        var w = measure(ctx, text, spec, px);
        if (w > maxW) { px *= maxW / w; }
        /* the coordinates in the set's own quieter tag colour, unless the
           layout asks for the ink (Headline, where they sit on the map) */
        ctx.fillStyle = key === 'tag' && !pos.ink ? (t.tag || rgba(t.ink, 0.6)) : t.ink;
        /* on the map itself (Headline): a halo of the ground's color, a
           hairline at the least, so a dense grid stays out of the letters */
        var hk = g.halo === true ? 1 : +g.halo || 0;   /* true = the full halo; a number scales it, 0 is none */
        var halo = hk ? { color: t.land, lw: hk * Math.max(0.06 * px, 0.004 * g.S) } : null;
        var drawn = drawLine(ctx, text, spec, px, cx, pos.y, halo);
        if (key === 'title' && pos.rule) {
          /* one rule under the title, a little wider than the word */
          var ry = pos.y + px * 0.26, half = drawn / 2 + px * 0.18, rlw = 0.0035 * g.S;
          ctx.lineCap = 'butt';
          ctx.beginPath(); ctx.moveTo(cx - half, ry); ctx.lineTo(cx + half, ry);
          if (halo) { ctx.strokeStyle = halo.color; ctx.lineWidth = rlw + halo.lw; ctx.stroke(); }
          ctx.strokeStyle = t.ink; ctx.lineWidth = rlw; ctx.stroke();
        }
        if (key === 'sub' && g.rules) {
          var mid = pos.y - px * 0.36, pad = px * 0.9;
          var left = g.map.x + g.map.w * 0.06, right = g.map.x + g.map.w * 0.94;
          ctx.lineWidth = 0.0035 * g.S;
          ctx.strokeStyle = t.ink;
          ctx.beginPath();
          if (cx - drawn / 2 - pad > left) { ctx.moveTo(left, mid); ctx.lineTo(cx - drawn / 2 - pad, mid); }
          if (cx + drawn / 2 + pad < right) { ctx.moveTo(cx + drawn / 2 + pad, mid); ctx.lineTo(right, mid); }
          ctx.stroke();
        }
      });
      setSpace(ctx, 0);
    }
  }

  /* clip a context to the map's shape (k = device px per unit) */
  function clipMap(ctx, g, k) {
    ctx.beginPath();
    if (g.shape === 'circle') ctx.arc((g.map.x + g.map.w / 2) * k, (g.map.y + g.map.h / 2) * k, g.map.w / 2 * k, 0, Math.PI * 2);
    else ctx.rect(g.map.x * k, g.map.y * k, g.map.w * k, g.map.h * k);
    ctx.clip();
  }

  /* a local face's stylesheet, added to the page the first time a design
     calls on it (the page's own link carries the Latin faces only) */
  var LOCAL_FACE = {}, _sheets = {};
  LOCAL.forEach(function (L) { if (!LOCAL_FACE[L.family]) LOCAL_FACE[L.family] = L; });
  function localSheet(L) {
    if (!L || !root.document || !document.head) return Promise.resolve();
    if (!_sheets[L.gf]) _sheets[L.gf] = new Promise(function (done) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=' + L.gf + '&display=swap';
      link.onload = link.onerror = function () { done(); };
      document.head.appendChild(link);
    });
    return _sheets[L.gf];
  }
  /* every face a design letters in, loaded before a canvas draws it — a
     canvas silently falls back to the system font when it is not ready.
     Each face is asked for the very words it draws: a CJK face comes in
     slices of characters, and only the slices holding them load. */
  function loadFonts(d) {
    if (!root.document || !document.fonts || !document.fonts.load) return Promise.resolve();
    var f = fontOf(d), sets = [];
    ['title', 'sub', 'tag'].forEach(function (k) {
      var raw = d && d[k], spec = lineSpec(f, k, raw, d), text = caseOf(String(raw || '').trim(), spec);
      sets.push([spec, text]);
      if (k === 'title') sets.push([Object.assign({}, spec, { weight: Math.min(800, spec.weight + 200) }), text]);
    });
    /* the faces each line calls on: its own, and its fallback only when the
       words hold letters of a script (the coordinates line's digits need
       no Japanese) */
    var faces = sets.map(function (s) { return [s[0].family].concat(s[0].fallback && scriptOf(s[1], d) ? [s[0].fallback] : []); });
    var sheets = [];
    faces.forEach(function (fams) { fams.forEach(function (fam) { if (LOCAL_FACE[fam]) sheets.push(localSheet(LOCAL_FACE[fam])); }); });
    return Promise.all(sheets).then(function () {
      var asks = [document.fonts.load('400 20px "Barlow"')];
      sets.forEach(function (s, i) {
        faces[i].forEach(function (fam) {
          asks.push(document.fonts.load(fontCss(Object.assign({}, s[0], { family: fam, fallback: null }), 40), s[1] || ' '));
        });
      });
      return Promise.all(asks);
    }).then(function () {}, function () {});
  }

  /* ── krdem:// — contour lines and summits off the elevation archive ── */
  /* (Trailhead, 2026-10-27.) A GeoJSON source whose data is a krdem:// URL
     gets its features from here: MapLibre hands the URL to this protocol
     (from its worker too, relayed to the page), the Mapterhorn tiles under
     the design's window are read into one elevation grid, and the grid is
     contoured (marching squares, chained into lines) or searched for its
     summits (the local maxima with real prominence). So every map — the
     editor's, its style cards, the print file — gets the same lines with
     no code of its own: it only has to wait for its sources, which it
     does. The URL names the window ROUNDED (the zoom to a half, the centre
     to an eighth of the window), so the editor's small pans reuse the same
     features (the maker re-points the sources when the window moves). */
  var DEM_TILE_CACHE = 24, _demTiles = {}, _demOrder = [], _demPms = {}, _demMemo = {}, _demMemoOrder = [];
  function latY(lat) { var s = Math.sin(lat * Math.PI / 180); return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI); }
  function yLat(y) { return Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI; }
  /* the window: the sheet at the design zoom, 1.3 times over, centred on
     the design, quantised so a small pan asks for the same features */
  function demWindow(d) {
    var inch = sizeInches(d.size, d.orient);
    var zoom = Math.round((+d.zoom || 12) * 2) / 2;
    var world = 512 * Math.pow(2, zoom);
    var hw = inch.w * UNITS_PER_IN * 0.65 / world, hh = inch.h * UNITS_PER_IN * 0.65 / world;
    var cx = (+d.lon + 180) / 360, cy = latY(+d.lat);
    var qx = hw / 4, qy = hh / 4;
    cx = Math.round(cx / qx) * qx; cy = Math.round(cy / qy) * qy;
    return { zoom: zoom, x0: cx - hw, x1: cx + hw, y0: cy - hh, y1: cy + hh };
  }
  /* drop: read the elevation that many zoom levels coarser (o.demDrop on
     a style). A style card is a hundred pixels across: at the design's own
     zoom its lines cost the whole poster's elevation tiles (Denver: 62
     reads, ~20 MB), two levels down a sixteenth of them. The editor and
     the print file leave it unset. */
  function demUrl(kind, d, terrainUrl, drop) {
    var w = demWindow(d);
    return 'krdem://' + kind + '?z=' + w.zoom + '&x0=' + w.x0.toFixed(7) + '&x1=' + w.x1.toFixed(7) +
      '&y0=' + w.y0.toFixed(7) + '&y1=' + w.y1.toFixed(7) + (drop ? '&dz=' + drop : '') + '&dem=' + encodeURIComponent(terrainUrl || DEFAULT_TERRAIN);
  }
  function demTile(url, z, x, y) {
    var key = url + '|' + z + '/' + x + '/' + y;
    if (_demTiles[key]) return _demTiles[key];
    if (!_demPms[url]) _demPms[url] = new root.pmtiles.PMTiles(pacedSource(url));
    var p = _demPms[url].getZxy(z, x, y).then(function (r) {
      if (!r || !r.data) return null;
      return root.createImageBitmap(new Blob([r.data])).then(function (bm) {
        var cv = document.createElement('canvas');
        cv.width = bm.width; cv.height = bm.height;
        var cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(bm, 0, 0);
        /* read the size before closing it: a closed bitmap reports 0x0 */
        var tw = bm.width, th = bm.height;
        var im = cx.getImageData(0, 0, tw, th);
        if (bm.close) bm.close();
        /* terrarium: (R * 256 + G + B / 256) - 32768 m; the sea is a flat
           plane just under zero in the archive, so it is clamped to 0 */
        var e = new Float32Array(tw * th), px = im.data;
        for (var i = 0, o = 0; i < e.length; i++, o += 4) {
          var v = (px[o] * 256 + px[o + 1] + px[o + 2] / 256) - 32768;
          e[i] = v < 0.5 ? 0 : v;
        }
        return { w: tw, h: th, e: e };
      });
    }).catch(function () { return null; });
    _demTiles[key] = p; _demOrder.push(key);
    while (_demOrder.length > DEM_TILE_CACHE) delete _demTiles[_demOrder.shift()];
    return p;
  }
  /* the grid: GW samples across the window, as many down as its shape
     asks, from the z tiles (z = the design zoom rounded up, 12 at most —
     the archive's deepest), blurred twice so a sampled grid's contour is
     not jagged at the sample size */
  function demGrid(url, w, GW, dz) {
    var z = Math.min(12, Math.max(3, Math.ceil(w.zoom) - (dz || 0)));
    var n = Math.pow(2, z);
    var x0 = Math.floor(w.x0 * n), x1 = Math.floor(w.x1 * n), y0 = Math.max(0, Math.floor(w.y0 * n)), y1 = Math.min(n - 1, Math.floor(w.y1 * n));
    var jobs = [], meta = [];
    for (var tx = x0; tx <= x1; tx++) for (var ty = y0; ty <= y1; ty++) {
      var wx = ((tx % n) + n) % n;
      meta.push({ x: tx, y: ty }); jobs.push(demTile(url, z, wx, ty));
    }
    return Promise.all(jobs).then(function (tiles) {
      var by = {};
      tiles.forEach(function (t, i) { if (t) by[meta[i].x + '|' + meta[i].y] = t; });
      var GH = Math.max(16, Math.min(1400, Math.round(GW * (w.y1 - w.y0) / (w.x1 - w.x0))));
      var g = new Float32Array(GW * GH), gx, gy;
      for (gy = 0; gy < GH; gy++) {
        var fy = (w.y0 + (w.y1 - w.y0) * gy / (GH - 1)) * n, tiy = Math.floor(fy);
        for (gx = 0; gx < GW; gx++) {
          var fx = (w.x0 + (w.x1 - w.x0) * gx / (GW - 1)) * n, tix = Math.floor(fx);
          var t = by[tix + '|' + tiy];
          if (!t) { g[gy * GW + gx] = 0; continue; }
          var px = Math.min(t.w - 1, Math.floor((fx - tix) * t.w)), py = Math.min(t.h - 1, Math.floor((fy - tiy) * t.h));
          g[gy * GW + gx] = t.e[py * t.w + px];
        }
      }
      for (var pass = 0; pass < 2; pass++) {
        var h = new Float32Array(g.length);
        for (gy = 0; gy < GH; gy++) for (gx = 0; gx < GW; gx++) {
          var s = 0, c = 0;
          for (var dy = -1; dy <= 1; dy++) for (var dx = -1; dx <= 1; dx++) {
            var yy = gy + dy, xx = gx + dx;
            if (yy < 0 || yy >= GH || xx < 0 || xx >= GW) continue;
            s += g[yy * GW + xx]; c++;
          }
          h[gy * GW + gx] = s / c;
        }
        g = h;
      }
      return { g: g, GW: GW, GH: GH, w: w };
    });
  }
  function gridLL(G, x, y) {
    var mx = G.w.x0 + (G.w.x1 - G.w.x0) * x / (G.GW - 1), my = G.w.y0 + (G.w.y1 - G.w.y0) * y / (G.GH - 1);
    return [+(mx * 360 - 180).toFixed(5), +yLat(my).toFixed(5)];
  }
  /* marching squares at every `step` metres, the segments chained into
     lines; `index` marks every fifth */
  function demContours(G, step) {
    var g = G.g, W = G.GW, H = G.GH, feats = [], i;
    var lo = Infinity, hi = -Infinity;
    for (i = 0; i < g.length; i++) { if (g[i] < lo) lo = g[i]; if (g[i] > hi) hi = g[i]; }
    for (var lv = Math.ceil(lo / step) * step; lv <= hi; lv += step) {
      if (lv <= 0) continue;
      var segs = [], ends = {};
      var pk = function (p) { return p[0].toFixed(3) + ',' + p[1].toFixed(3); };
      var cut = function (xa, ya, va, xb, yb, vb) { var t = (lv - va) / (vb - va); return [xa + (xb - xa) * t, ya + (yb - ya) * t]; };
      for (var y = 0; y < H - 1; y++) for (var x = 0; x < W - 1; x++) {
        var a = g[y * W + x], b = g[y * W + x + 1], c = g[(y + 1) * W + x + 1], dd = g[(y + 1) * W + x];
        var code = (a >= lv ? 8 : 0) | (b >= lv ? 4 : 0) | (c >= lv ? 2 : 0) | (dd >= lv ? 1 : 0);
        if (code === 0 || code === 15) continue;
        var top = cut(x, y, a, x + 1, y, b), right = cut(x + 1, y, b, x + 1, y + 1, c),
            bottom = cut(x, y + 1, dd, x + 1, y + 1, c), left = cut(x, y, a, x, y + 1, dd), ss;
        switch (code) {
          case 1: case 14: ss = [[left, bottom]]; break;
          case 2: case 13: ss = [[bottom, right]]; break;
          case 3: case 12: ss = [[left, right]]; break;
          case 4: case 11: ss = [[top, right]]; break;
          case 5: ss = [[top, left], [bottom, right]]; break;
          case 6: case 9: ss = [[top, bottom]]; break;
          case 7: case 8: ss = [[top, left]]; break;
          default: ss = [[top, right], [left, bottom]];
        }
        for (i = 0; i < ss.length; i++) {
          var id = segs.length; segs.push({ a: ss[i][0], b: ss[i][1], used: false });
          var ka = pk(ss[i][0]), kb = pk(ss[i][1]);
          (ends[ka] = ends[ka] || []).push(id); (ends[kb] = ends[kb] || []).push(id);
        }
      }
      for (var s0 = 0; s0 < segs.length; s0++) {
        if (segs[s0].used) continue;
        segs[s0].used = true;
        var line = [segs[s0].a, segs[s0].b];
        for (var dir = 0; dir < 2; dir++) {
          var cur = dir ? line[line.length - 1] : line[0];
          for (;;) {
            var k = pk(cur), nxt = null, list = ends[k] || [];
            for (i = 0; i < list.length; i++) if (!segs[list[i]].used) { nxt = segs[list[i]]; break; }
            if (!nxt) break;
            nxt.used = true;
            var other = pk(nxt.a) === k ? nxt.b : nxt.a;
            if (dir) line.push(other); else line.unshift(other);
            cur = other;
          }
        }
        if (line.length < 4) continue;
        feats.push({ type: 'Feature', properties: { ele: lv, index: (Math.round(lv / step) % 5) === 0 ? 1 : 0 },
          geometry: { type: 'LineString', coordinates: line.map(function (p) { return gridLL(G, p[0], p[1]); }) } });
      }
    }
    return { type: 'FeatureCollection', features: feats };
  }
  /* the summits: a cell higher than everything within R cells, with at
     least `prom` metres of drop in that reach, the `cap` highest, each a
     triangle `units` design units tall in the coordinates ink over a
     slightly larger one in the paper (halo), sized at the window's zoom */
  function demSummits(G, R, prom, cap, units) {
    var g = G.g, W = G.GW, H = G.GH, found = [], x, y;
    for (y = R; y < H - R; y += 2) for (x = R; x < W - R; x += 2) {
      var e = g[y * W + x]; if (e < 30) continue;
      var top = true, low = e;
      for (var dy = -R; dy <= R && top; dy += 2) for (var dx = -R; dx <= R; dx += 2) {
        var v = g[(y + dy) * W + x + dx];
        if (v > e) { top = false; break; }
        if (v < low) low = v;
      }
      if (top && e - low >= prom) found.push({ x: x, y: y, e: e });
    }
    found.sort(function (a, b) { return b.e - a.e; });
    var keep = [];
    found.forEach(function (f) {
      if (keep.length >= cap) return;
      for (var i = 0; i < keep.length; i++) if (Math.abs(keep[i].x - f.x) <= R && Math.abs(keep[i].y - f.y) <= R) return;
      keep.push(f);
    });
    var world = 512 * Math.pow(2, G.w.zoom), feats = [];
    keep.forEach(function (f) {
      var mx = G.w.x0 + (G.w.x1 - G.w.x0) * f.x / (W - 1), my = G.w.y0 + (G.w.y1 - G.w.y0) * f.y / (H - 1);
      [[1, 1.35], [0, 1]].forEach(function (hs) {
        var s = units * hs[1] / world, h = s * 0.866;
        var ring = [[mx, my - h * 0.6], [mx + s / 2, my + h * 0.4], [mx - s / 2, my + h * 0.4]].map(function (p) {
          return [+(p[0] * 360 - 180).toFixed(6), +yLat(p[1]).toFixed(6)];
        });
        ring.push(ring[0]);
        feats.push({ type: 'Feature', properties: { ele: Math.round(f.e), halo: hs[0] }, geometry: { type: 'Polygon', coordinates: [ring] } });
      });
    });
    return { type: 'FeatureCollection', features: feats };
  }
  var _demProto = false;
  function ensureDemProtocol() {
    if (_demProto) return true;
    if (!root.maplibregl || !root.pmtiles || typeof document === 'undefined' || !root.createImageBitmap) return false;
    root.maplibregl.addProtocol('krdem', function (params) {
      var u = String(params.url), m = u.match(/^krdem:\/\/(\w+)\?(.*)$/);
      if (!m) return Promise.reject(new Error('krdem: bad url'));
      var kind = m[1], q = {};
      m[2].split('&').forEach(function (kv) { var at = kv.indexOf('='); if (at > 0) q[kv.slice(0, at)] = decodeURIComponent(kv.slice(at + 1)); });
      var w = { zoom: +q.z, x0: +q.x0, x1: +q.x1, y0: +q.y0, y1: +q.y1 };
      var near = w.zoom >= 13;
      if (!_demMemo[u]) {
        /* a coarser read (dz, a style card's) samples a smaller grid too */
        var dz = Math.max(0, Math.min(4, +q.dz || 0));
        _demMemo[u] = demGrid(q.dem || DEFAULT_TERRAIN, w, dz ? 256 : (near ? 448 : 640), dz).then(function (G) {
          if (kind === 'summits') return demSummits(G, near ? 40 : 28, near ? 60 : 150, 12, 7);
          var step = near ? 20 : (w.zoom < 10.5 ? 100 : 50);
          return demContours(G, step);
        });
        _demMemoOrder.push(u);
        while (_demMemoOrder.length > 8) delete _demMemo[_demMemoOrder.shift()];
      }
      return _demMemo[u].then(function (fc) { return { data: fc }; });
    });
    _demProto = true;
    return true;
  }

  /* ── pmtiles protocol, registered once ──────────────────────────────── */
  var _proto = null;
  function ensureProtocol() {
    if (_proto) return true;
    if (!root.maplibregl || !root.pmtiles) return false;
    _proto = new root.pmtiles.Protocol();
    root.maplibregl.addProtocol('pmtiles', _proto.tile);
    /* archives opened before MapLibre was on the page (prefetch) */
    for (var u in _added) _proto.add(_added[u]);
    return true;
  }

  /* TILE REQUESTS, PACED. A whole-metro frame with every building asks for
     ~400 ranges at once (Chicago: 216 planet + 183 buildings) and peaked at
     235 in flight; a few hundred more and Chrome starts refusing them
     (net::ERR_INSUFFICIENT_RESOURCES, seen while testing deeper tiles) —
     each refusal a hole in the map. So every archive is read through one
     queue of at most MAX_FETCH requests, and a refused or dropped request
     is tried twice more before it counts as an error. */
  var MAX_FETCH = 48, _active = 0, _waiting = [];
  /* tile traffic so far this page (KRPoster.fetchStats) — what a session
     costs on the worker: one request each, bytes as served. busyMs is the
     time at least one request was on the wire, so bytes over busyMs is how
     fast tiles arrive (the maker's slow-connection check: Safari has no
     navigator.connection) */
  var _stats = { requests: 0, bytes: 0, retries: 0, failed: 0, busyMs: 0 };
  var _busyFrom = 0;
  function _now() { return root.performance && root.performance.now ? root.performance.now() : Date.now(); }
  function _acquire() {
    if (_active < MAX_FETCH) {
      if (!_active) _busyFrom = _now();
      _active++;
      return Promise.resolve();
    }
    return new Promise(function (ok) { _waiting.push(ok); });
  }
  function _release() {
    var next = _waiting.shift();
    if (next) next();
    else if (!--_active) _stats.busyMs += _now() - _busyFrom;
  }
  /* THE LAST FEW READS, KEPT (RECENT_MAX bytes in all): the same range
     asked again comes from memory. Two maps on the page read the same
     tiles (the editor's coarse first map and the style cards), and a
     coarse tile stretched over a new zoom is asked for again. Each caller
     gets its own copy, so nothing handed on can empty the kept one. */
  var RECENT_MAX = 8388608, _recent = {}, _recentKeys = [], _recentBytes = 0;
  /* reads on the wire that nothing can call off (prefetch's): the same
     range asked meanwhile waits for them instead of going out twice */
  var _pending = {};
  function recentCopy(r) { return { data: r.data.slice(0), etag: r.etag, cacheControl: r.cacheControl, expires: r.expires }; }
  function recentKeep(key, r) {
    var n = r && r.data ? r.data.byteLength : 0;
    if (!n || n > RECENT_MAX / 8 || _recent[key]) return;
    _recent[key] = recentCopy(r);
    _recentKeys.push(key); _recentBytes += n;
    while (_recentBytes > RECENT_MAX) {
      var old = _recentKeys.shift();
      _recentBytes -= _recent[old].data.byteLength;
      delete _recent[old];
    }
  }
  function pacedSource(url) {
    var src = new root.pmtiles.FetchSource(url);
    var once = function (offset, length, signal, etag, tries) {
      var key = url + '|' + offset + '|' + length;
      if (_recent[key]) return Promise.resolve(recentCopy(_recent[key]));
      if (_pending[key]) return _pending[key].then(recentCopy);
      var read = _acquire().then(function () {
        return src.getBytes(offset, length, signal, etag).then(function (r) {
          _release();
          _stats.requests++; _stats.bytes += (r && r.data && r.data.byteLength) || 0;
          recentKeep(key, r);
          return r;
        }, function (e) {
          _release();
          if (tries < 2) _stats.retries++; else _stats.failed++;
          var aborted = (signal && signal.aborted) || (e && e.name === 'AbortError');
          if (aborted || tries >= 2 || (e && /etag/i.test(e.name || ''))) throw e;
          return new Promise(function (ok) { setTimeout(ok, 400 * (tries + 1)); })
            .then(function () { return once(offset, length, signal, etag, tries + 1); });
        });
      });
      if (!signal) {
        _pending[key] = read;
        var done = function () { delete _pending[key]; };
        read.then(done, done);
      }
      return read;
    };
    return { getKey: function () { return url; }, getBytes: function (o, l, sg, et) { return once(o, l, sg, et, 0); } };
  }
  /* an archive's reader, one per URL, reading through the queue: made
     once the PMTiles script is on the page, and handed to MapLibre's
     'pmtiles://' protocol once that is (ensureProtocol) */
  var _added = {};
  function archiveOf(url) {
    if (!url || !root.pmtiles) return null;
    if (!_added[url]) {
      _added[url] = new root.pmtiles.PMTiles(pacedSource(url));
      if (_proto) _proto.add(_added[url]);
    }
    return _added[url];
  }
  /* registers an archive once, so 'pmtiles://' + url reads through the queue */
  function ensureArchive(url) {
    if (url && ensureProtocol()) archiveOf(url);
  }
  /* an archive's own bounds [west, south, east, north], from its header
     (its first read, which MapLibre makes anyway); null if it can't be read */
  function archiveBounds(url) {
    var a = archiveOf(url);
    if (!a) return Promise.resolve(null);
    return a.getHeader().then(function (h) { return [h.minLon, h.minLat, h.maxLon, h.maxLat]; }, function () { return null; });
  }
  /* reads the tiles [x0..x1] x [y0..y1] at zoom z ahead of the map, with
     only the PMTiles script on the page: their bytes wait in the kept reads
     above, where MapLibre's own ask finds them (the maker's coarse first
     map, read while MapLibre, ~270 KB, is still on its way) */
  function prefetch(url, z, x0, y0, x1, y1) {
    var a = archiveOf(url), n = Math.pow(2, z);
    if (!a) return;
    for (var x = x0; x <= x1; x++) for (var y = Math.max(0, y0); y <= Math.min(n - 1, y1); y++) {
      a.getZxy(z, ((x % n) + n) % n, y).catch(function () {});
    }
  }

  /* (the print file is made on Kevin's PC, never on the live page: the
     renderer is docs/poster-maker/print/kr-poster-print.js) */

  /* ── thumbnails: one class render, every theme mixed per pixel ──────── */
  function hexRgb(hex) {
    var h = String(hex).replace('#', ''); var n = parseInt(h, 16);
    return [n >> 16 & 255, n >> 8 & 255, n & 255];
  }
  /* src = ImageData rendered in CLASS_THEME; returns ImageData in theme t.
     A pixel's red/green/blue are its shares of land/water/park and the rest
     is road, because lines and fills blend linearly in the framebuffer. */
  function recolor(src, t, out) {
    var L = hexRgb(t.land), Wt = hexRgb(t.water), P = hexRgb(t.park), R = hexRgb(t.road);
    var a = src.data, b = out.data;
    for (var i = 0; i < a.length; i += 4) {
      var l = a[i] / 255, w = a[i + 1] / 255, p = a[i + 2] / 255, r = 1 - l - w - p;
      if (r < 0) r = 0;
      b[i] = L[0] * l + Wt[0] * w + P[0] * p + R[0] * r;
      b[i + 1] = L[1] * l + Wt[1] * w + P[1] * p + R[1] * r;
      b[i + 2] = L[2] * l + Wt[2] * w + P[2] * p + R[2] * r;
      b[i + 3] = 255;
    }
    return out;
  }

  /* ── coordinates and the saved design ───────────────────────────────── */
  function fmtCoords(lat, lon) {
    return Math.abs(lat).toFixed(3) + '°' + (lat >= 0 ? 'N' : 'S') + ' / ' +
      Math.abs(lon).toFixed(3) + '°' + (lon >= 0 ? 'E' : 'W');
  }
  /* local: a place in a country with a script of its own — { cc, title,
     sub } in that script and en: [title, sub] as the search gave them, so a
     saved design can still switch its lettering both ways */
  var KEYS = ['lat', 'lon', 'zoom', 'bearing', 'theme', 'layout', 'font', 'orient', 'size', 'title', 'sub', 'tag', 'tagAuto', 'place', 'buildings', 'lines', 'marker',
    'state', 'share', 'ladder', 'finish', 'relief', 'local'];
  function pack(d) {
    var o = { v: 1 };
    KEYS.forEach(function (k) { if (d[k] != null && d[k] !== '') o[k] = d[k]; });
    /* only a whole state carries its share and ladder */
    if (!o.state) { delete o.share; delete o.ladder; }
    if (!o.relief || o.state) delete o.relief;
    else if (typeof o.share === 'number') o.share = +o.share.toFixed(3);
    if (typeof o.lat === 'number') o.lat = +o.lat.toFixed(6);
    if (typeof o.lon === 'number') o.lon = +o.lon.toFixed(6);
    if (typeof o.zoom === 'number') o.zoom = +o.zoom.toFixed(3);
    if (typeof o.bearing === 'number') o.bearing = +o.bearing.toFixed(1);
    if (o.marker) o.marker = { icon: o.marker.icon, lat: +(+o.marker.lat).toFixed(6), lon: +(+o.marker.lon).toFixed(6) };
    if (o.local && (o.state || !localFor(o.local.cc))) delete o.local;
    else if (o.local) o.local = { cc: o.local.cc, title: o.local.title || '', sub: o.local.sub || '', en: o.local.en || [] };
    return o;
  }
  function encode(d) {
    var s = JSON.stringify(pack(d));
    var b = btoa(unescape(encodeURIComponent(s)));
    return b.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function decode(str) {
    try {
      var b = String(str).replace(/-/g, '+').replace(/_/g, '/');
      while (b.length % 4) b += '=';
      var o = JSON.parse(decodeURIComponent(escape(atob(b))));
      return (o && typeof o.lat === 'number' && typeof o.lon === 'number') ? o : null;
    } catch (e) { return null; }
  }

  root.KRPoster = {
    version: 1,
    UNITS_PER_IN: UNITS_PER_IN,
    DEFAULT_PMTILES: DEFAULT_PMTILES,
    THEMES: THEMES, THEME_GROUPS: THEME_GROUPS, FONTS: FONTS, LAYOUTS: LAYOUTS, CLASS_THEME: CLASS_THEME,
    theme: themeByKey,
    font: function (k) { return FONT_BY[k] || null; },
    LOCAL: LOCAL, localFor: localFor, localCountry: localCountry, localName: localName, localSheet: localSheet, scriptOf: scriptOf,
    sizeInches: sizeInches,
    ozOf: ozOf, ozFor: ozFor, ozForPhone: ozForPhone, ozForView: ozForView, camZoom: camZoom,
    fetchStats: function () { return Object.assign({ active: _active, waiting: _waiting.length }, _stats, { busyMs: _stats.busyMs + (_active ? _now() - _busyFrom : 0) }); },
    archiveBounds: archiveBounds, prefetch: prefetch,
    geom: geom,
    style: style,
    drawOverlay: drawOverlay,
    DEFAULT_TERRAIN: DEFAULT_TERRAIN, RELIEF: RELIEF, reliefStyle: reliefStyle,
    demUrl: demUrl, ensureDemProtocol: ensureDemProtocol,
    FINISHES: FINISHES, finish: function (k) { return FINISH_BY[k] || null; }, finishOf: finishOf,
    drawFinish: drawFinish, drawFinishLayer: drawFinishLayer, drawPlate: drawPlate, drawPlateOff: drawPlateOff,
    clipMap: clipMap,
    loadFonts: loadFonts,
    ensureProtocol: ensureProtocol,
    recolor: recolor,
    fmtCoords: fmtCoords,
    MARKERS: MARKERS, markerPath: markerPath, posterXY: posterXY, aimLat: aimLat, camLat: camLat,
    STATE_LADDER: STATE_LADDER, STATE_SHARE: STATE_SHARE,
    stateBox: stateBox, stateFit: stateFit, statePlace: statePlace,
    stateClass: stateClass, stateShare: stateShare, stateRecolor: stateRecolor,
    pack: pack, encode: encode, decode: decode
  };
})(typeof window !== 'undefined' ? window : this);
